import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback,
  useRef,
} from "react";
import API_CONFIG from "../config/api.js";
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Space,
  message,
  Tag,
  Row,
  Col,
  Spin,
  Tabs,
  Descriptions,
  Layout,
  Progress,
  Divider,
  Typography,
  List,
  Table,
  Card,
  Tooltip,
  Empty,
  Alert,
  Switch,
  Upload,
  Dropdown,
  Badge,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  QrcodeOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
  UndoOutlined,
  SaveOutlined,
  FilterOutlined,
  ReloadOutlined,
  BarChartOutlined,
  WalletOutlined,
  ClockCircleOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelection } from "../SelectionContext";
import BarcodeScanner from "./BarcodeScanner";
import OrderProductSearch from "../components/orders/OrderProductSearch";
import useOrderFormOptions, { fetchOrderFormOptions, useOrderChoiceResource } from "../components/orders/useOrderFormOptions";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import useBusinessMutationInvalidation from "../hooks/useBusinessMutationInvalidation";
import { purchaseFormData } from "../components/orders/orderFormData";
import usePaymentFormChoices from "../gestion-payments/usePaymentFormChoices";
import useTaxes from "./useTaxes";

import { AchatsContext } from "./store/context";
import * as XLSX from "xlsx";
import { useAuth } from "../context/AuthContext"; // Use AuthContext

import {
  AchatFormData,
  Product,
  PurchasePackagingProfile,
  PurchaseSummary,
  AchatsAction,
  AchatsState,
  OrderDetailModalProps,
  AchatFormProps,
  AchatsTableProps,
} from "./types";
import PaymentFormModal from "./components/PaymentFormModal";
import RowActionsMenu from "../components/RowActionsMenu";
import OrderInitialPaymentFields, {
  buildInitialPaymentPayload,
} from "../components/orders/OrderInitialPaymentFields";
import {
  CommercialDocumentAdditionalInfo,
  CommercialDocumentModalTitle,
  CommercialDocumentSummary,
} from "../components/orders/CommercialDocumentShell";
import {
  buildCommercialEntitySearchText,
  getCommercialEntityDisplayName,
  matchesCommercialEntitySearch,
} from "../utils/entityUtils";
import { getSelectedProductionTerminology } from "../GestionProductions/productionTerminology";
import StockReplenishmentReturnModal from "../components/StockReplenishmentReturnModal";
import { isReturnableStockReplenishment } from "../utils/stockReplenishment";
import { discardSalesDraftFromReplenishment } from "../utils/salesReplenishmentDraft";
import CommercialListSummary from "../components/orders/CommercialListSummary";
import "../components/orders/CommercialListPage.css";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const UNIT_PRICE_DECIMAL_PLACES = 4;
const QUANTITY_DECIMAL_PLACES = 5;
const MIN_PURCHASE_QUANTITY = 0.00001;
type PurchaseFlowOrderType = "purchase" | "purchase_order";
interface GestionAchatProps {
  orderType?: PurchaseFlowOrderType;
  moduleLabel?: string;
  moduleLabelPlural?: string;
}

const ORDER_TYPE_PERMISSION_ROOT_MAP: Record<PurchaseFlowOrderType, string> = {
  purchase: "Gestion Commerciale.Approvisionnement.Achats.Achat",
  purchase_order: "Gestion Commerciale.Approvisionnement.Achats.BonCommande",
};

const normalizePurchaseFlowOrderType = (
  value?: string
): PurchaseFlowOrderType =>
  value === "purchase_order" ? "purchase_order" : "purchase";

const capitalizeLabel = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const getFlowLabels = (orderType: PurchaseFlowOrderType) => {
  if (orderType === "purchase_order") {
    return {
      singular: "bon de commande",
      plural: "bons de commande",
      singularTitle: "Bon de commande",
      pluralTitle: "Bons de commande",
    };
  }

  return {
    singular: "achat",
    plural: "achats",
    singularTitle: "Achat",
    pluralTitle: "Achats",
  };
};

const toNumberWithDecimalSupport = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized =
    typeof value === "string"
      ? value.replace(/\s/g, "").replace(/,/g, ".")
      : value;
  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const compactColumnTitle = (label: string, tooltip: string) => (
  <Space size={4}>
    <span>{label}</span>
    <Tooltip title={tooltip}>
      <QuestionCircleOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
    </Tooltip>
  </Space>
);

const normalizePurchasePackagingProfiles = (
  profiles?: PurchasePackagingProfile[]
): PurchasePackagingProfile[] =>
  (Array.isArray(profiles) ? profiles : [])
    .map((profile) => ({
      id:
        profile?.id !== null &&
        profile?.id !== undefined &&
        Number.isFinite(Number(profile.id))
          ? Number(profile.id)
          : null,
      label: String(profile?.label || "").trim(),
      contained_quantity: toNumberWithDecimalSupport(
        profile?.contained_quantity
      ),
      reference_purchase_price: toNumberWithDecimalSupport(
        profile?.reference_purchase_price
      ),
      is_default:
        profile?.is_default === true ||
        profile?.is_default === 1 ||
        profile?.is_default === "1",
    }))
    .filter((profile) => profile.label && profile.contained_quantity > 0);

const getDefaultPurchasePackagingProfile = (
  profiles?: PurchasePackagingProfile[]
): PurchasePackagingProfile | null => {
  const normalizedProfiles = normalizePurchasePackagingProfiles(profiles);
  return (
    normalizedProfiles.find((profile) => profile.is_default) ||
    normalizedProfiles[0] ||
    null
  );
};

const sanitizeDecimalInput = (
  value: any,
  maxDecimals: number = UNIT_PRICE_DECIMAL_PLACES
): string => {
  if (value === null || value === undefined) return "";

  const normalized = String(value)
    .replace(/\s/g, "")
    .replace(/,/g, ".")
    .replace(/[^0-9.]/g, "");

  if (!normalized) return "";

  const parts = normalized.split(".");
  const integerPart = parts[0] || "0";
  const decimalPart = parts.slice(1).join("").slice(0, maxDecimals);

  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
};

const normalizePositiveQuantity = (
  value: any,
  fallback: number = MIN_PURCHASE_QUANTITY
): number => {
  const numericValue = toNumberWithDecimalSupport(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return fallback;
  }

  return Number(numericValue.toFixed(QUANTITY_DECIMAL_PLACES));
};

interface StockReplenishmentItem {
  product_id?: number | string;
  product_name?: string;
  missing_quantity?: number | string;
  purchase_price?: number | string;
  unit_id?: number | string | null;
  unit_name?: string;
  unit_short_name?: string;
}

interface StockReplenishmentPrefill {
  request_id?: string;
  target_order_type?: PurchaseFlowOrderType;
  company_id?: number | string | null;
  warehouse_id?: number | string | null;
  production_unit_id?: number | string | null;
  production_unit_name?: string;
  output_quantity?: number | string | null;
  source_path?: string;
  source_type?: string;
  source_label?: string;
  return_context?: Record<string, any>;
  items?: StockReplenishmentItem[];
}

const findPurchaseProduct = (
  products: Product[],
  productId: number
): Product | undefined =>
  products.find(
    (product) =>
      Number(product?.id) === productId ||
      Number(product?.id_produit) === productId ||
      Number(product?.product_id) === productId
  );

const mapStockReplenishmentItems = (
  rawItems: StockReplenishmentItem[] = [],
  products: Product[] = []
): AchatFormData["produitsAches"] => {
  const linesByProductId = new Map<
    number,
    AchatFormData["produitsAches"][number]
  >();

  rawItems.forEach((item) => {
    const productId = Number(item?.product_id);
    const missingQuantity = toNumberWithDecimalSupport(
      item?.missing_quantity
    );

    if (
      !Number.isFinite(productId) ||
      productId <= 0 ||
      !Number.isFinite(missingQuantity) ||
      missingQuantity <= 0
    ) {
      return;
    }

    const existingLine = linesByProductId.get(productId);
    if (existingLine) {
      existingLine.quantite = normalizePositiveQuantity(
        existingLine.quantite + missingQuantity
      );
      return;
    }

    const product = findPurchaseProduct(products, productId);
    const rawUnitId =
      item?.unit_id ?? product?.unit_id ?? product?.unit?.id ?? null;
    const parsedUnitId =
      rawUnitId === null || rawUnitId === undefined
        ? null
        : Number(rawUnitId);
    const unitId =
      parsedUnitId !== null && Number.isFinite(parsedUnitId)
        ? parsedUnitId
        : null;

    linesByProductId.set(productId, {
      produit_id: productId,
      nom_produit:
        item?.product_name ||
        product?.name ||
        product?.nom_produit ||
        product?.product_name ||
        `Produit ${productId}`,
      quantite: normalizePositiveQuantity(missingQuantity),
      prix_unitaire_HT: toNumberWithDecimalSupport(
        item?.purchase_price ?? product?.purchase_price ?? 0
      ),
      remise: 0,
      taxe: null,
      montant_taxe: 0,
      unit_id: unitId,
      unit_name:
        item?.unit_name || product?.unit_name || product?.unit?.name || "",
      unit_short_name:
        item?.unit_short_name ||
        product?.unit_short_name ||
        product?.unit?.short_name ||
        "",
      purchase_packaging_profiles: normalizePurchasePackagingProfiles(
        product?.purchase_packaging_profiles
      ),
      purchase_entry_mode: "direct",
      selected_purchase_packaging_id: null,
      purchase_packaging_label: "",
      purchase_packaging_count: undefined,
    });
  });

  return Array.from(linesByProductId.values());
};

const formatUnitPrice = (value: any): string => {
  const numericValue = toNumberWithDecimalSupport(value);
  return numericValue.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: UNIT_PRICE_DECIMAL_PLACES,
  });
};

// Fonction utilitaire pour formater les nombres
const formatNumber = (value: any): string => {
  // Gestion des cas null ou undefined
  if (value === null || value === undefined) return "0";

  // Conversion en nombre si nécessaire
  const numericValue = toNumberWithDecimalSupport(value);

  // Vérification que la valeur est bien un nombre
  if (isNaN(numericValue)) return "0";

  // Formatage avec locale fr-FR, sans décimales
  const shouldShowDecimals = Math.abs(numericValue % 1) > 0;
  return numericValue.toLocaleString("fr-FR", {
    minimumFractionDigits: shouldShowDecimals ? 2 : 0,
    maximumFractionDigits: shouldShowDecimals ? UNIT_PRICE_DECIMAL_PLACES : 0,
  });
};

const formatQuantity = (value: any): string => {
  const numericValue = toNumberWithDecimalSupport(value);
  return numericValue.toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: QUANTITY_DECIMAL_PLACES,
  });
};

// Formatage des MONTANTS TOTAUX : arrondi a l'entier (le CFA/XOF n'a pas de
// sous-unite). Evite les decimales parasites de virgule flottante sur les
// totaux (ex. 280 000,0003 -> 280 000). Reserve aux totaux (synthese + colonnes
// Total / Montant paye) ; les prix unitaires de lignes gardent formatNumber.
const formatTotalAmount = (value: any): string => {
  const numericValue = toNumberWithDecimalSupport(value);
  if (isNaN(numericValue)) return "0";
  return Math.round(numericValue).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const normalizePurchaseSummary = (summary?: any | null): PurchaseSummary => ({
  total_purchase_count: toNumberWithDecimalSupport(
    summary?.total_purchase_count ?? summary?.total_sales_count
  ),
  total_purchase_amount: toNumberWithDecimalSupport(
    summary?.total_purchase_amount ?? summary?.total_sales_amount
  ),
  paid_amount: toNumberWithDecimalSupport(summary?.paid_amount),
  unpaid_amount: toNumberWithDecimalSupport(summary?.unpaid_amount),
  partially_paid_amount: toNumberWithDecimalSupport(
    summary?.partially_paid_amount
  ),
  partially_due_amount: toNumberWithDecimalSupport(
    summary?.partially_due_amount ??
      Math.max(
        toNumberWithDecimalSupport(summary?.total_due_amount) -
          toNumberWithDecimalSupport(summary?.unpaid_amount),
        0
      )
  ),
  total_paid_amount: toNumberWithDecimalSupport(
    summary?.total_paid_amount ?? summary?.total_received_amount
  ),
  total_due_amount: toNumberWithDecimalSupport(summary?.total_due_amount),
});

const emptyPurchaseSummary = normalizePurchaseSummary(null);

const BASE_URL = API_CONFIG.BASE_URL;

// FIX: Helper function to get authentication headers (pattern from GestionVente)
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const PURCHASE_DEPENDENCY_LABELS: Record<string, string> = {
  production: "production",
  transfer: "transfert",
  purchase_return: "retour fournisseur",
  sale: "vente",
  stock_exit: "sortie de stock",
  inventory_shortage: "stock déjà consommé",
};

const buildApiError = async (
  response: Response,
  fallback: string
): Promise<Error> => {
  const payload = await response.json().catch(() => null);
  const baseMessage =
    payload?.error || payload?.message || fallback || response.statusText;

  if (payload?.code !== "PURCHASE_DEPENDENCY_CONFLICT") {
    return new Error(
      typeof payload?.details === "string" ? payload.details : baseMessage
    );
  }

  const summary = payload?.details?.dependency_summary;
  const dependencyText =
    summary && typeof summary === "object"
      ? Object.entries(summary)
          .filter(([, count]) => Number(count) > 0)
          .map(([type, count]) => {
            const label = PURCHASE_DEPENDENCY_LABELS[type] || type;
            return `${Number(count)} ${label}${Number(count) > 1 ? "s" : ""}`;
          })
          .join(", ")
      : "";

  return new Error(
    dependencyText
      ? `${baseMessage} Dépendances détectées : ${dependencyText}.`
      : baseMessage
  );
};

// Fonction pour récupérer les informations du magasin par son ID
const fetchWarehouseInfo = async (warehouseId: number | null): Promise<any> => {
  if (!warehouseId) return null;

  try {
    // FIX: URL corrigée - utiliser /api/warehouses/ (PLURAL) au lieu de /api/warehouse/
    const response = await fetch(`${BASE_URL}/api/warehouses/${warehouseId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      // console.error(
      //   `Erreur lors de la récupération du magasin ${warehouseId}: ${response.statusText}`
      // );

      // Créer un objet magasin minimal avec l'ID comme fallback
      console.warn(
        `Impossible de récupérer les informations complètes de l'établissement ${warehouseId}, utilisation d'un objet minimal`
      );
      return {
        id: warehouseId,
        name: `Établissement ${warehouseId}`,
      };
    }

    // FIX: Ajouter return statement manquant pour retourner les données du warehouse
    const warehouse = await response.json();
    return warehouse;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération de l'établissement ${warehouseId}:`,
      error
    );

    // En cas d'erreur, retourner également un objet minimal
    return {
      id: warehouseId,
      name: `Établissement ${warehouseId}`,
    };
  }
};

/* --- API --- */
// Exporté pour être réutilisé par l'écran mobile (src/mobile/achats/AchatsMobile.jsx).
export const api = {
  async fetchAchats(params: any) {
    const resolvedOrderType: PurchaseFlowOrderType =
      params.order_type === "purchase_order" ? "purchase_order" : "purchase";
    const queryParams: any = {
      order_type: resolvedOrderType,
      // Ajouter tri par défaut comme les ventes
      sort_by: "order_date",
      order: "desc",
    };
    if (params.invoice_number)
      queryParams.invoice_number = params.invoice_number;
    const search = String(params.search || "").trim();
    if (search) queryParams.search = search;
    if (params.fournisseur) {
      queryParams.fournisseur = params.fournisseur;
    }
    if (params.dateDebut) queryParams.dateDebut = params.dateDebut;
    if (params.dateFin) queryParams.dateFin = params.dateFin;
    if (params.page) queryParams.page = params.page;
    if (params.limit) queryParams.limit = params.limit;
    if (params.warehouse) queryParams.warehouse = params.warehouse;
    if (params.include_deleted)
      queryParams.include_deleted = params.include_deleted;
    if (params.payment_status) {
      queryParams.payment_status = params.payment_status;
    }
    if (params.include_summary) {
      queryParams.include_summary = params.include_summary;
    }
    // Ajouter le paramètre company_id s'il est fourni
    if (params.company_id) {
      queryParams.company_id = params.company_id;
    }

    const qs = new URLSearchParams(queryParams).toString();

    try {
      const response = await fetch(`${BASE_URL}/api/orders?${qs}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Erreur HTTP ${response.status}: ${errorText}`);
        throw new Error(
          `${response.status} ${response.statusText}: ${errorText}`
        );
      }

      const data = await response.json();

      if (!data || (!data.orders && !Array.isArray(data))) {
        throw new Error("Format de réponse invalide");
      }

      const achatsArray = Array.isArray(data.orders)
        ? data.orders
        : Array.isArray(data)
        ? data
        : [];
      const total = data.total || achatsArray.length;

      return { data: achatsArray, total, summary: data.summary };
    } catch (error) {
      // console.error("Erreur lors de la récupération des achats:", error);
      throw error;
    }
  },
  async fetchPurchaseOrders(params: any = {}) {
    return api.fetchAchats({
      ...params,
      order_type: "purchase_order",
      payment_status: undefined,
    });
  },
  async fetchFournisseurs(warehouseId?: number) {
    // Construire l'URL avec les paramètres nécessaires
    let url = `${BASE_URL}/api/users/suppliers`;

    // Ajouter le paramètre warehouseId si fourni
    if (warehouseId) {
      url += `?warehouseId=${warehouseId}`;
    } else {
    }

    try {
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();
      // console.log("Fournisseurs récupérés :", data);

      // Vérifier la structure des données reçues
      let fournisseursData = Array.isArray(data) ? data : [];
      if (data && data.users && Array.isArray(data.users)) {
        fournisseursData = data.users;
      }

      // Ajouter des logs pour débogage
      // console.log(
      //   "Structure des données de fournisseurs:",
      //   fournisseursData.length > 0 ? fournisseursData[0] : "Aucun fournisseur"
      // );
      if (fournisseursData.length > 0) {
      }

      // Dédoublonnage des fournisseurs (correction des doublons en BDD)
      const { deduplicateEntities, countDuplicates } = await import('../utils/entityUtils');
      const uniqueSuppliers = deduplicateEntities(fournisseursData);

      // Log pour monitoring (seulement si des doublons détectés)
      const stats = countDuplicates(fournisseursData);
      if (stats.duplicates > 0) {
        console.warn(`[fetchFournisseurs] Doublons détectés et corrigés: ${stats.duplicates} sur ${stats.total} entrées`);
      }

      return uniqueSuppliers;
    } catch (error) {
      console.error("Erreur lors de la récupération des fournisseurs:", error);
      return []; // Retourner un tableau vide plutôt que de propager l'erreur
    }
  },
  async fetchProduits(params: any = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${BASE_URL}/api/produits?${queryString}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(response.statusText);
    const data = await response.json();
    // console.log("Produits récupérés :", data);
    return { data: data.products || [], total: data.total || 0 };
  },
  async fetchUnits(companyId?: number) {
    const queryParams = new URLSearchParams({
      limit: "500",
      ...(companyId ? { company_id: String(companyId) } : {}),
    });

    const response = await fetch(`${BASE_URL}/api/units?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(response.statusText);
    const data = await response.json();
    return data.units || [];
  },
  async fetchTaxes(companyId?: number) {
    // Construire l'URL avec les paramètres
    let url = `${BASE_URL}/api/taxes`;

    if (companyId) {
      url += `?companyId=${companyId}`;
      // console.log(`Récupération des taxes pour l'entreprise ${companyId}`);
    } else {
      // console.log(
      //   "Récupération de toutes les taxes (aucune entreprise spécifiée)"
      // );
    }

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(response.statusText);
    const data = await response.json();
    // console.log("Taxes récupérées :", data);
    return data.taxes || [];
  },
  async uploadPurchaseDocument(
    file: File,
    orderType: PurchaseFlowOrderType = "purchase",
    action: "create" | "edit" = "create"
  ) {
    const uploadData = new FormData();
    uploadData.append("document", file);
    const token = localStorage.getItem("authToken");
    const query = new URLSearchParams({
      order_type: orderType,
      action,
    });
    const response = await fetch(
      `${BASE_URL}/api/orders/purchase-documents/upload?${query.toString()}`,
      {
        method: "POST",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: uploadData,
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || "Impossible de telecharger le justificatif."
      );
    }
    return response.json();
  },
  async deletePurchaseDocument(
    key: string,
    orderType: PurchaseFlowOrderType = "purchase",
    action: "create" | "edit" = "create"
  ) {
    const query = new URLSearchParams({
      order_type: orderType,
      action,
    });
    const response = await fetch(
      `${BASE_URL}/api/orders/purchase-documents/${encodeURIComponent(
        key
      )}?${query.toString()}`,
      {
        method: "DELETE",
        headers: getAuthHeaders(),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || "Impossible de supprimer le justificatif."
      );
    }
    return response.json();
  },
  async createAchat(data: any, orderType: PurchaseFlowOrderType = "purchase") {
    data.order_type = orderType;
    // console.log("Création de l'achat avec les données :", data);
    const response = await fetch(`${BASE_URL}/api/orders`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw await buildApiError(
        response,
        "Erreur lors de la création de l'achat"
      );
    }
    return await response.json();
  },
  async updateAchat(
    id: number,
    data: any,
    orderType: PurchaseFlowOrderType = "purchase"
  ) {
    data.order_type = orderType;
    // console.log(
    //   "Mise à jour de l'achat (ID =",
    //   id,
    //   ") avec les données :",
    //   data
    // );

    // Vérifier que les données sont complètes
    if (!data.warehouse_id) {
      // console.error("warehouse_id manquant", data);
      throw new Error("Veuillez sélectionner un établissement");
    }

    if (!data.user_id) {
      // console.error("user_id (fournisseur) manquant", data);
      throw new Error("Veuillez sélectionner un fournisseur");
    }

    if (!data.company_id) {
      // console.error("company_id manquant", data);
      throw new Error("Veuillez sélectionner une entreprise");
    }

    if (!data.items || data.items.length === 0) {
      // console.error("items (produits) manquants", data);
      throw new Error("Veuillez ajouter au moins un produit");
    }

    try {
      const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw await buildApiError(
          response,
          "Erreur lors de la mise à jour de l'achat"
        );
      }

      return await response.json();
    } catch (error) {
      // console.error("Erreur lors de la mise à jour de l'achat:", error);
      throw error;
    }
  },
  async deleteAchat(id: number) {
    const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw await buildApiError(
        response,
        "Erreur lors de la suppression de l'achat"
      );
    }
    return await response.json();
  },
  async cancelAchat(id: number) {
    const payload = { order_status: "Annulé", cancelled: 1, is_deletable: 0 };
    const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw await buildApiError(
        response,
        "Cette commande ne peut pas être annulée directement"
      );
    }
    return await response.json();
  },
  async fetchAchatDetail(id: string) {
    const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(response.statusText);
    const data = await response.json();
    return data;
  },
  async fetchOrderItems(orderId: string) {
    try {
      const response = await fetch(`${BASE_URL}/api/orders/${orderId}/items`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        console.error(
          "Erreur lors de la récupération des éléments:",
          response.statusText
        );
        return [];
      }
      const data = await response.json();
      return Array.isArray(data) ? data : data.items || [];
    } catch (error) {
      // console.error("Exception lors de la récupération des éléments:", error);
      return [];
    }
  },
  async restoreAchat(id: number) {
    const response = await fetch(`${BASE_URL}/api/orders/${id}/restore`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw await buildApiError(
        response,
        "Erreur lors de la restauration de l'achat"
      );
    }
    return await response.json();
  },
  async fetchWarehouses() {
    try {
      const response = await fetch(`${BASE_URL}/api/warehouses`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();
      // console.log("Magasins récupérés :", data);
      return data;
    } catch (error) {
      // console.error("Erreur lors de la récupération des magasins:", error);
      throw error;
    }
  },
  async fetchWarehouse(id: number) {
    try {
      const response = await fetch(`${BASE_URL}/api/warehouses/${id}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();
      // console.log(`Magasin ${id} récupéré :`, data);
      return data;
    } catch (error) {
      // console.error(`Erreur lors de la récupération du magasin ${id}:`, error);
      throw error;
    }
  },
};

/* --- Reducer et état initial --- */
const initialState: AchatsState = {
  achats: [],
  fournisseurs: [],
  purchaseOrders: [],
  produits: [],
  taxes: [],
  loading: false,
  error: null,
  currentPage: 1,
  itemsPerPage: 10,
  totalPages: 1,
  searchTerm: "",
  dateRange: { start: "", end: "" },
  selectedFournisseur: null,
  showForm: false,
  showScanner: false,
  formData: {
    Date_Facture: dayjs().format("YYYY-MM-DD"),
    due_date: dayjs().format("YYYY-MM-DD"),
    Statut_Achat: "Commandé",
    Fournisseur_ID: undefined,
    warehouse_id: null,
    produitsAches: [],
    remise_globale: 0,
    termes_conditions: "",
    remarques: "",
  },
  showDeleted: false,
  activeTab: "all",
  purchaseSummary: emptyPurchaseSummary,
};

const achatsReducer = (
  state: AchatsState,
  action: AchatsAction
): AchatsState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ACHATS":
      return {
        ...state,
        achats: action.payload.achats,
        purchaseSummary: normalizePurchaseSummary(action.payload.summary),
        totalPages: Math.ceil(action.payload.total / state.itemsPerPage),
        loading: false,
        error: null,
      };
    case "DELETE_ACHAT":
      return {
        ...state,
        achats: state.achats.filter((achat) => achat.id !== action.payload),
      };
    case "SET_FOURNISSEURS":
      return { ...state, fournisseurs: action.payload, loading: false };
    case "SET_PURCHASE_ORDERS":
      return { ...state, purchaseOrders: action.payload, loading: false };
    case "SET_PRODUITS":
      return { ...state, produits: action.payload, loading: false };
    case "SET_TAXES":
      return { ...state, taxes: action.payload, loading: false };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "SET_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_ITEMS_PER_PAGE":
      return { ...state, itemsPerPage: action.payload, currentPage: 1 };
    case "SET_SEARCH":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "SET_DATE_RANGE":
      return { ...state, dateRange: action.payload, currentPage: 1 };
    case "SET_SELECTED_FOURNISSEUR":
      return { ...state, selectedFournisseur: action.payload, currentPage: 1 };
    case "TOGGLE_FORM":
      return { ...state, showForm: action.payload };
    case "TOGGLE_SCANNER":
      return { ...state, showScanner: action.payload };
    case "SET_FORM_DATA":
      return { ...state, formData: action.payload };
    case "UPDATE_FORM_DATA":
      return {
        ...state,
        formData: { ...state.formData, ...action.payload },
      };
    case "TOGGLE_SHOW_DELETED":
      return { ...state, showDeleted: action.payload, currentPage: 1 };
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload };
    default:
      return state;
  }
};

/* --- Panneau de synthese des achats --- */
const PurchaseSummaryPanel: React.FC<{
  summary: PurchaseSummary;
  moduleLabel: string;
  moduleLabelPlural: string;
}> = ({ summary, moduleLabel, moduleLabelPlural }) => {
  const formatSummaryAmount = (value: number) =>
    formatTotalAmount(value).replace(/[\u00a0\u202f]/g, " ");
  const metrics = [
    {
      key: "total",
      label: `Total des ${moduleLabelPlural}`,
      value: `${formatSummaryAmount(summary.total_purchase_amount)} CFA`,
      icon: <BarChartOutlined />,
      tone: "primary" as const,
      description: `Montant total des ${moduleLabelPlural} correspondant aux filtres actifs.`,
    },
    {
      key: "paid",
      label: "Montant pay\u00e9",
      value: `${formatSummaryAmount(summary.paid_amount)} CFA`,
      icon: <WalletOutlined />,
      tone: "success" as const,
      description: `Montant déjà réglé sur les ${moduleLabelPlural} filtrés.`,
    },
    {
      key: "unpaid",
      label: "Montant impay\u00e9",
      value: `${formatSummaryAmount(summary.unpaid_amount)} CFA`,
      icon: <ClockCircleOutlined />,
      tone: "danger" as const,
      description: `Montant des ${moduleLabelPlural} encore entièrement impayés.`,
    },
    {
      key: "partial",
      label: "Reste \u00e0 payer sur paiements partiels",
      value: `${formatSummaryAmount(summary.partially_due_amount)} CFA`,
      icon: <PieChartOutlined />,
      tone: "warning" as const,
      description: "Solde restant sur les règlements partiels.",
    },
  ];
  return (
    <CommercialListSummary
      ariaLabel={`Situation financière des ${moduleLabelPlural}`}
      metrics={metrics}
    />
  );
};

/* --- Modal de detail de commande --- */
const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  order,
  visible,
  onClose,
  produits,
  taxes,
  fournisseurs,
  orderType = "purchase",
  showPayments = true,
  moduleLabel,
  moduleLabelPlural,
  refreshOrderDetails,
  refreshAchats,
}) => {
  const effectiveOrderType = normalizePurchaseFlowOrderType(orderType);
  const flowLabels = getFlowLabels(effectiveOrderType);
  const resolvedModuleLabel = moduleLabel || flowLabels.singular;
  const resolvedModuleLabelPlural = moduleLabelPlural || flowLabels.plural;
  const { hasPermission } = useAuth();
  const showPaymentSummary =
    showPayments && effectiveOrderType === "purchase";
  const canViewPayments = hasPermission(
    "Gestion Commerciale.Approvisionnement.Achats.Achat.view_payments"
  );
  const canCreateOutgoingPayment = hasPermission(
    "Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.create"
  );
  const showPaymentSection =
    showPaymentSummary && canViewPayments;

  const [activeTab, setActiveTab] = useState("details");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [localOrder, setLocalOrder] = useState<any>(null);

  useEffect(() => {
    if (visible && order) {
      setLocalOrder(order);
      setActiveTab("details");
    }
  }, [order, visible]);

  const openPaymentDetails = (paymentId: number | string | undefined) => {
    if (paymentId === undefined || paymentId === null) {
      message.error("Identifiant de paiement introuvable");
      return;
    }

    window.open(`/paiements-sortants?payment_id=${paymentId}`, "_blank");
  };

  const openPaymentModal = () => {
    if (!canCreateOutgoingPayment) {
      message.warning(
        "Vous n'avez pas la permission d'ajouter un paiement fournisseur."
      );
      return;
    }
    setShowPaymentModal(true);
  };

  if (!localOrder) return null;

  const supplierByUserId = fournisseurs.find(
    (f: any) => Number(f.user_id) === Number(localOrder.user_id)
  );
  const supplierById = !supplierByUserId
    ? fournisseurs.find((f: any) => Number(f.id) === Number(localOrder.user_id))
    : null;
  const supplierToDisplay = supplierByUserId || supplierById;

  const supplierName =
    localOrder.supplier_name ||
    supplierToDisplay?.name ||
    supplierToDisplay?.company_name ||
    supplierToDisplay?.Nom_Raison_Sociale ||
    "Fournisseur inconnu";

  const orderItems = Array.isArray(localOrder.items)
    ? localOrder.items
    : Array.isArray(localOrder.produitsAches)
    ? localOrder.produitsAches
    : [];

  const payments = Array.isArray(localOrder.payments) ? localOrder.payments : [];

  const calculateOrderTotals = () => {
    const baseHT = orderItems.reduce((sum: number, item: any) => {
      const quantity = Number(item.quantity ?? item.quantite ?? 0);
      const unitPrice = Number(item.unit_price ?? item.prix_unitaire_HT ?? 0);
      return sum + quantity * unitPrice;
    }, 0);

    const remisesIndividuelles = orderItems.reduce((sum: number, item: any) => {
      return sum + Number(item.discount_rate ?? item.remise ?? 0);
    }, 0);

    const remiseGlobale = Number(localOrder.discount ?? localOrder.remise_globale ?? 0);
    const totalRemises = remisesIndividuelles + remiseGlobale;
    const netHT = baseHT - totalRemises;

    const computedTaxes = orderItems.reduce((sum: number, item: any) => {
      const quantity = Number(item.quantity ?? item.quantite ?? 0);
      const unitPrice = Number(item.unit_price ?? item.prix_unitaire_HT ?? 0);
      const remise = Number(item.discount_rate ?? item.remise ?? 0);
      const itemTaxId = item.tax_id ?? item.taxe;

      const taxFromList = taxes.find(
        (t: any) => Number(t.id) === Number(itemTaxId)
      );
      const taxRate = Number(item.tax_rate ?? taxFromList?.rate ?? 0);
      const montantHT = quantity * unitPrice;
      const netLigne = montantHT - remise;
      return sum + (netLigne * taxRate) / 100;
    }, 0);

    const totalTaxes = Number(localOrder.tax_amount ?? computedTaxes);
    const totalTTC = Number(localOrder.total ?? netHT + totalTaxes);
    const paidAmount = Number(localOrder.paid_amount ?? 0);
    const amountDue = totalTTC - paidAmount;

    // Frais annexes (transport, assurance, douane, autres). Les frais "inclus"
    // sont ajoutes au total/du fournisseur ; tous entrent dans le cout de revient.
    const feeTransport = Number(localOrder.extra_cost_transport ?? 0);
    const feeAssurance = Number(localOrder.extra_cost_assurance ?? 0);
    const feeDouane = Number(localOrder.extra_cost_douane ?? 0);
    const feeAutres = Number(localOrder.extra_cost_autres ?? 0);
    const feeTransportIncluded =
      Number(localOrder.extra_cost_transport_included ?? 1) !== 0;
    const feeAssuranceIncluded =
      Number(localOrder.extra_cost_assurance_included ?? 1) !== 0;
    const feeDouaneIncluded =
      Number(localOrder.extra_cost_douane_included ?? 1) !== 0;
    const feeAutresIncluded =
      Number(localOrder.extra_cost_autres_included ?? 1) !== 0;
    const extraCostTotal = Number(
      localOrder.extra_cost_total ??
        feeTransport + feeAssurance + feeDouane + feeAutres
    );
    const extraCostIncludedTotal = Number(
      localOrder.extra_cost_included_total ?? 0
    );
    // TTC marchandise = total du fournisseur - frais "inclus" : se reconcilie
    // toujours exactement avec le Montant Total TTC affiche.
    const merchandiseTTC = totalTTC - extraCostIncludedTotal;

    return {
      baseHT,
      totalRemises,
      netHT,
      totalTaxes,
      merchandiseTTC,
      totalTTC,
      paidAmount,
      amountDue,
      feeTransport,
      feeAssurance,
      feeDouane,
      feeAutres,
      feeTransportIncluded,
      feeAssuranceIncluded,
      feeDouaneIncluded,
      feeAutresIncluded,
      extraCostTotal,
      extraCostIncludedTotal,
    };
  };

  const {
    baseHT,
    totalRemises,
    netHT,
    totalTaxes,
    merchandiseTTC,
    totalTTC,
    paidAmount,
    amountDue,
    feeTransport,
    feeAssurance,
    feeDouane,
    feeAutres,
    feeTransportIncluded,
    feeAssuranceIncluded,
    feeDouaneIncluded,
    feeAutresIncluded,
    extraCostTotal,
    extraCostIncludedTotal,
  } = calculateOrderTotals();

  const paymentStatus =
    localOrder.payment_status ||
    (paidAmount <= 0
      ? "Non payé"
      : paidAmount < totalTTC
      ? "Partiellement payé"
      : "Payé");

  const getOrderStatusTag = (status: string) => {
    let color = "green";
    if (status === "Annulé") color = "red";
    if (status === "En attente") color = "orange";
    return <Tag color={color}>{status || "Non spécifié"}</Tag>;
  };

  const getPaymentStatusTag = (status: string) => {
    let color = "default";
    let displayText = status;

    if (status === "Non payé") {
      color = "red";
      displayText = "Non payé";
    } else if (status === "Partiel" || status === "Partiellement payé") {
      color = "orange";
      displayText = "Partiellement payé";
    } else if (status === "Payé") {
      color = "green";
      displayText = "Payé";
    }

    return <Tag color={color}>{displayText || "Non spécifié"}</Tag>;
  };

  const productColumns = [
    {
      title: "Produit",
      dataIndex: "product_id",
      key: "product",
      render: (productId: any, record: any): string => {
        if (record.product_name || record.nom_produit) {
          return record.product_name || record.nom_produit;
        }

        const resolvedId = productId ?? record.product_id ?? record.produit_id;
        const product = produits.find(
          (p: any) =>
            Number(p.id) === Number(resolvedId) ||
            Number(p.id_produit) === Number(resolvedId)
        );

        return product?.name || `Produit ${resolvedId ?? "inconnu"}`;
      },
    },
    {
      title: "Quantité",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
      render: (value: any, record: any): string =>
        formatQuantity(value ?? record.quantite ?? 0),
    },
    {
      title: "Prix unitaire HT",
      dataIndex: "unit_price",
      key: "unit_price",
      align: "right" as const,
      render: (value: any, record: any): string =>
        `${formatUnitPrice(value ?? record.prix_unitaire_HT ?? 0)} CFA`,
    },
    {
      title: "Montant HT",
      key: "montant_ht",
      align: "right" as const,
      render: (record: any): string => {
        const quantity = Number(record.quantity ?? record.quantite ?? 0);
        const unitPrice = Number(record.unit_price ?? record.prix_unitaire_HT ?? 0);
        return `${formatNumber(quantity * unitPrice)} CFA`;
      },
    },
    {
      title: "Remise",
      key: "remise",
      align: "right" as const,
      render: (record: any): string =>
        `${formatNumber(record.discount_rate ?? record.remise ?? 0)} CFA`,
    },
    {
      title: "Net HT",
      key: "net_ht",
      align: "right" as const,
      render: (record: any): string => {
        const quantity = Number(record.quantity ?? record.quantite ?? 0);
        const unitPrice = Number(record.unit_price ?? record.prix_unitaire_HT ?? 0);
        const remise = Number(record.discount_rate ?? record.remise ?? 0);
        const montantHT = quantity * unitPrice;
        return `${formatNumber(montantHT - remise)} CFA`;
      },
    },
    {
      title: "Taxe",
      dataIndex: "tax_id",
      key: "tax",
      align: "center" as const,
      render: (taxId: any, record: any): string => {
        if (record.tax_name) {
          return `${record.tax_name} (${record.tax_rate ?? 0}%)`;
        }
        if (!taxId && !record.taxe) return "Aucune";

        const resolvedTaxId = taxId ?? record.taxe;
        const tax = taxes.find((t: any) => Number(t.id) === Number(resolvedTaxId));
        return tax ? `${tax.name} (${tax.rate}%)` : "Taxe inconnue";
      },
    },
    {
      title: "Montant Taxe",
      key: "montant_taxe",
      align: "right" as const,
      render: (record: any): string => {
        const quantity = Number(record.quantity ?? record.quantite ?? 0);
        const unitPrice = Number(record.unit_price ?? record.prix_unitaire_HT ?? 0);
        const remise = Number(record.discount_rate ?? record.remise ?? 0);
        const taxId = record.tax_id ?? record.taxe;
        const tax = taxes.find((t: any) => Number(t.id) === Number(taxId));
        const taxRate = Number(record.tax_rate ?? tax?.rate ?? 0);
        const montantHT = quantity * unitPrice;
        const netHTLine = montantHT - remise;
        const taxAmount = (netHTLine * taxRate) / 100;
        return `${formatNumber(taxAmount)} CFA`;
      },
    },
    {
      title: "Total TTC",
      key: "total_ttc",
      align: "right" as const,
      render: (record: any) => {
        const quantity = Number(record.quantity ?? record.quantite ?? 0);
        const unitPrice = Number(record.unit_price ?? record.prix_unitaire_HT ?? 0);
        const remise = Number(record.discount_rate ?? record.remise ?? 0);
        const taxId = record.tax_id ?? record.taxe;
        const tax = taxes.find((t: any) => Number(t.id) === Number(taxId));
        const taxRate = Number(record.tax_rate ?? tax?.rate ?? 0);
        const montantHT = quantity * unitPrice;
        const netHTLine = montantHT - remise;
        const taxAmount = (netHTLine * taxRate) / 100;
        return <strong>{formatNumber(netHTLine + taxAmount)} CFA</strong>;
      },
    },
  ];

  const totalPayments = payments.reduce((sum: number, payment: any) => {
    return (
      sum +
      Number(
        payment.payment_amount ??
          payment.montant_affecte ??
          payment.amount_applied ??
          payment.amount ??
          payment.op_amount ??
          0
      )
    );
  }, 0);

  const paymentColumns = [
    {
      title: "N° Paiement",
      dataIndex: "payment_number",
      key: "payment_number",
      render: (text: string, record: any) =>
        text ||
        `Paiement #${record.payment_id ?? record.order_payment_id ?? record.id ?? "N/A"}`,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Montant",
      key: "payment_amount",
      render: (_: any, record: any) =>
        `${formatNumber(
          record.payment_amount ??
            record.montant_affecte ??
            record.amount_applied ??
            record.amount ??
            record.op_amount ??
            0
        )} CFA`,
    },
    {
      title: "Mode de paiement",
      dataIndex: "payment_mode_name",
      key: "payment_mode_name",
      render: (mode: string) => mode || "Non spécifié",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => {
        const paymentId = record.payment_id ?? record.order_payment_id ?? record.id;
        return (
          <Button
            type="link"
            onClick={() => openPaymentDetails(paymentId)}
            disabled={!paymentId}
          >
            Détails
          </Button>
        );
      },
    },
  ];

  const handlePaymentAdded = async (closeDetailModal?: boolean) => {
    setShowPaymentModal(false);

    if (!localOrder?.id) {
      return;
    }

    const hideLoading = message.loading(
      "Mise à jour des informations de paiement...",
      0
    );

    try {
      const updatedOrder = await refreshOrderDetails(String(localOrder.id));
      if (updatedOrder) {
        setLocalOrder(updatedOrder);
      }
      await refreshAchats();
      hideLoading();
      message.success("Paiement ajouté avec succès");

      if (closeDetailModal === true) {
        onClose();
      }
    } catch (error) {
      hideLoading();
      console.error("Erreur lors du rafraîchissement des données:", error);
      message.error(
        "Le paiement est enregistré, mais l'actualisation du détail a échoué."
      );
    }
  };

  const openPurchaseDocument = async () => {
    if (!localOrder?.id || !localOrder?.document_url) return;
    const hideLoading = message.loading("Ouverture du justificatif...", 0);
    try {
      const response = await fetch(
        `${BASE_URL}/api/orders/${localOrder.id}/document`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || "Impossible d'ouvrir le justificatif."
        );
      }
      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Impossible d'ouvrir le justificatif."
      );
    } finally {
      hideLoading();
    }
  };

  return (
    <>
      <Modal
        visible={visible}
        title={`Détails du ${resolvedModuleLabel} #${
          localOrder.invoice_number || localOrder.id
        }`}
        onCancel={onClose}
        width={1000}
        footer={[
          <Button key="close" onClick={onClose}>
            Fermer
          </Button>,
        ]}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Informations générales" key="details">
            <Card title={`Informations du ${resolvedModuleLabel}`}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="N° Facture">
                  {localOrder.invoice_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Date">
                  {localOrder.order_date
                    ? dayjs(localOrder.order_date).format("DD/MM/YYYY")
                    : "-"}
                </Descriptions.Item>
                {effectiveOrderType === "purchase" && (
                  <Descriptions.Item label="Échéance de paiement">
                    {localOrder.due_date
                      ? dayjs(localOrder.due_date).format("DD/MM/YYYY")
                      : "Non renseignée"}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Fournisseur">
                  {supplierName}
                </Descriptions.Item>
                <Descriptions.Item label="Établissement">
                  {localOrder.warehouse_name || "Non spécifié"}
                </Descriptions.Item>
                <Descriptions.Item label="Statut achat">
                  {getOrderStatusTag(localOrder.order_status)}
                </Descriptions.Item>
                {showPaymentSummary && (
                  <Descriptions.Item label="Statut paiement">
                    {getPaymentStatusTag(paymentStatus)}
                  </Descriptions.Item>
                )}
                {localOrder.purchase_order_invoice_number && (
                  <Descriptions.Item label="Bon de commande lié">
                    {localOrder.purchase_order_invoice_number}
                  </Descriptions.Item>
                )}
                {effectiveOrderType === "purchase" && (
                  <Descriptions.Item label="Pièce justificative">
                    {localOrder.document_url ? (
                      <Button type="link" onClick={openPurchaseDocument}>
                        {localOrder.document_name || "Ouvrir le document"}
                      </Button>
                    ) : (
                      "Aucun document"
                    )}
                  </Descriptions.Item>
                )}
                {effectiveOrderType === "purchase_order" && (
                  <Descriptions.Item label="Achats liés">
                    {localOrder.linked_purchase_count || 0}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Card title="Résumé financier" style={{ marginTop: 16 }}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Montant Base HT">
                  {formatNumber(baseHT)} CFA
                </Descriptions.Item>
                <Descriptions.Item label="Total Remises">
                  {formatNumber(totalRemises)} CFA
                </Descriptions.Item>
                <Descriptions.Item label="Montant Net HT" span={2}>
                  <div
                    style={{
                      backgroundColor: "#f0f2f5",
                      padding: "8px",
                      borderRadius: "4px",
                      fontWeight: "bold",
                      fontSize: "16px",
                      color: "#1890ff",
                    }}
                  >
                    {formatNumber(netHT)} CFA
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Total Taxes">
                  {formatNumber(totalTaxes)} CFA
                </Descriptions.Item>
                {extraCostTotal > 0 && (
                  <Descriptions.Item label="Montant TTC marchandise" span={2}>
                    {formatNumber(merchandiseTTC)} CFA
                  </Descriptions.Item>
                )}
                {extraCostTotal > 0 && feeTransport > 0 && (
                  <Descriptions.Item label="Frais de transport" span={2}>
                    {formatNumber(feeTransport)} CFA{" "}
                    <Tag color={feeTransportIncluded ? "green" : "orange"}>
                      {feeTransportIncluded ? "Inclus au total" : "Suivi séparé"}
                    </Tag>
                  </Descriptions.Item>
                )}
                {extraCostTotal > 0 && feeAssurance > 0 && (
                  <Descriptions.Item label="Frais d'assurance" span={2}>
                    {formatNumber(feeAssurance)} CFA{" "}
                    <Tag color={feeAssuranceIncluded ? "green" : "orange"}>
                      {feeAssuranceIncluded ? "Inclus au total" : "Suivi séparé"}
                    </Tag>
                  </Descriptions.Item>
                )}
                {extraCostTotal > 0 && feeDouane > 0 && (
                  <Descriptions.Item label="Frais de douane" span={2}>
                    {formatNumber(feeDouane)} CFA{" "}
                    <Tag color={feeDouaneIncluded ? "green" : "orange"}>
                      {feeDouaneIncluded ? "Inclus au total" : "Suivi séparé"}
                    </Tag>
                  </Descriptions.Item>
                )}
                {extraCostTotal > 0 && feeAutres > 0 && (
                  <Descriptions.Item label="Autres frais" span={2}>
                    {formatNumber(feeAutres)} CFA{" "}
                    <Tag color={feeAutresIncluded ? "green" : "orange"}>
                      {feeAutresIncluded ? "Inclus au total" : "Suivi séparé"}
                    </Tag>
                  </Descriptions.Item>
                )}
                {extraCostTotal > 0 && (
                  <Descriptions.Item label="Total frais annexes" span={2}>
                    <strong>{formatNumber(extraCostTotal)} CFA</strong>{" "}
                    <span style={{ color: "#888", fontSize: 12 }}>
                      (dont {formatNumber(extraCostIncludedTotal)} inclus au dû
                      fournisseur,{" "}
                      {formatNumber(extraCostTotal - extraCostIncludedTotal)}{" "}
                      suivis séparément)
                    </span>
                  </Descriptions.Item>
                )}
                <Descriptions.Item
                  label={
                    extraCostTotal > 0
                      ? "Montant Total TTC (dû fournisseur)"
                      : "Montant Total TTC"
                  }
                >
                  <strong style={{ color: "#52c41a", fontSize: "18px" }}>
                    {formatNumber(totalTTC)} CFA
                  </strong>
                </Descriptions.Item>
                {showPaymentSummary && (
                  <Descriptions.Item label="Montant Payé">
                    <span style={{ color: "#52c41a", fontWeight: "bold" }}>
                      {formatNumber(paidAmount)} CFA
                    </span>
                  </Descriptions.Item>
                )}
                {showPaymentSummary && (
                  <Descriptions.Item label="Montant Restant">
                    <strong
                      style={{
                        color: amountDue > 0 ? "#ff4d4f" : "#52c41a",
                        fontSize: "16px",
                      }}
                    >
                      {formatNumber(amountDue)} CFA
                    </strong>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Divider orientation="left">
              {`${resolvedModuleLabelPlural} détaillés`}
            </Divider>
            <Table
              dataSource={orderItems}
              columns={productColumns}
              rowKey={(record: any, index) =>
                String(
                  record.id ??
                    `${record.product_id ?? record.produit_id ?? "item"}-${index}`
                )
              }
              pagination={false}
              size="small"
              scroll={{ x: "max-content" }}
              locale={{
                emptyText: `Aucun produit trouvé pour ce ${resolvedModuleLabel}.`,
              }}
            />

            <Divider orientation="left">Notes et termes</Divider>
            <Card>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Remarques">
                  {localOrder.remarques || localOrder.notes || "Aucune remarque"}
                </Descriptions.Item>
                <Descriptions.Item label="Termes et conditions">
                  {localOrder.termes_conditions ||
                    localOrder.terms_condition ||
                    "Aucun terme spécifié"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Tabs.TabPane>

          {showPaymentSection && (
            <Tabs.TabPane tab="Paiements" key="payments">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 16,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <h3 style={{ margin: 0 }}>
                  Total des paiements: {formatNumber(totalPayments)} CFA
                </h3>
                {amountDue > 0 &&
                  !localOrder.is_deleted &&
                  canCreateOutgoingPayment && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openPaymentModal}
                    disabled={
                      paymentStatus === "Payé" ||
                      localOrder.order_status === "Annulé"
                    }
                  >
                    Ajouter un paiement
                  </Button>
                )}
              </div>
              <Table
                dataSource={payments}
                columns={paymentColumns}
                rowKey={(record: any, index) =>
                  String(
                    record.payment_id ??
                      record.order_payment_id ??
                      record.id ??
                      `payment-${index}`
                  )
                }
                pagination={false}
                size="small"
                locale={{
                  emptyText:
                    "Aucun paiement enregistré pour cet achat.",
                }}
              />
            </Tabs.TabPane>
          )}
        </Tabs>
      </Modal>

      {showPaymentSection &&
        canCreateOutgoingPayment &&
        showPaymentModal && (
        <PaymentFormModal
          visible={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          order={localOrder}
          onPaymentAdded={handlePaymentAdded}
        />
      )}
    </>
  );
};

/* --- Formulaire modal de création/modification --- */
export const AchatForm: React.FC<AchatFormProps> = ({
  visible,
  onClose,
  refreshAchats,
  onReplenishmentCompleted,
  onReplenishmentCancelled,
  orderType = "purchase",
  moduleLabel,
  moduleLabelPlural,
}) => {
  const context = useContext(AchatsContext);
  if (!context) throw new Error("AchatForm must be used within AchatsContext");
  const { state, dispatch, api } = context;
  const { hasPermission, user } = useAuth();
  const effectiveOrderType = normalizePurchaseFlowOrderType(orderType);
  const flowLabels = getFlowLabels(effectiveOrderType);
  const resolvedModuleLabel = moduleLabel || flowLabels.singular;
  const resolvedModuleLabelTitle = capitalizeLabel(resolvedModuleLabel);
  const isPurchaseFlow = effectiveOrderType === "purchase";
  const canCreateInitialPayment =
    isPurchaseFlow &&
    hasPermission(
      "Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.create",
    );
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [purchaseDocument, setPurchaseDocument] = useState<{
    url: string;
    name: string;
    key?: string;
    isNew?: boolean;
  } | null>(null);
  const [purchaseOrderSearch, setPurchaseOrderSearch] = useState("");
  const [appliedPurchaseOrderSearch, setAppliedPurchaseOrderSearch] = useState("");
  const [purchaseOrderPage, setPurchaseOrderPage] = useState(1);
  const [loadingLinkedPurchaseOrderItems, setLoadingLinkedPurchaseOrderItems] =
    useState(false);
  const linkedPurchaseOrderRequestRef = useRef(0);
  const { selectedWarehouse: selectedWarehouseId, selectedCompany } =
    useSelection();
  const companyContextId = Number((selectedCompany as any)?.id ?? selectedCompany);
  const warehouseContextId = Number((selectedWarehouseId as any)?.id ?? selectedWarehouseId);
  const watchedSupplier = Form.useWatch("Fournisseur_ID", form);
  const immediatePaymentEnabled = Boolean(Form.useWatch("add_initial_payment", form));
  const rawSupplierId = watchedSupplier?.value ?? watchedSupplier ?? state.formData.Fournisseur_ID;
  const supplierChoiceId = typeof rawSupplierId === "object" ? rawSupplierId?.value : rawSupplierId;
  const canSubmitForm = hasPermission(`${ORDER_TYPE_PERMISSION_ROOT_MAP[effectiveOrderType]}.${state.formData.id ? "edit" : "create"}`);
  const choices = useOrderFormOptions({ orderType: effectiveOrderType, action: state.formData.id ? "edit" : "create",
    active: visible, enabled: canSubmitForm, entityId: Number(supplierChoiceId) || null, items: state.formData.produitsAches });
  const initialPaymentChoices = usePaymentFormChoices({ ...choices.scope, paymentType: "out", includeEntities: false,
    active: visible && isPurchaseFlow && !state.formData.id && immediatePaymentEnabled, enabled: canCreateInitialPayment });
  const watchedPurchaseOrder = Form.useWatch("purchase_order_id", form);
  const selectedPurchaseOrder = watchedPurchaseOrder ?? state.formData.purchase_order_id ?? state.formData.Bon_Commande_ID;
  useEffect(() => {
    const timer = setTimeout(() => { setAppliedPurchaseOrderSearch(purchaseOrderSearch.trim()); setPurchaseOrderPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [purchaseOrderSearch]);
  const purchaseOrders = useOrderChoiceResource({ orderType: effectiveOrderType, action: state.formData.id ? "edit" : "create",
    active: visible && isPurchaseFlow, enabled: canSubmitForm,
    parameters: { kind: "purchase-orders", search: appliedPurchaseOrderSearch, page: purchaseOrderPage, limit: 20,
      ...(selectedPurchaseOrder ? { selected_id: selectedPurchaseOrder } : {}) } });
  const loadingPurchaseOrders = purchaseOrders.loading;
  useEffect(() => {
    const selected = choices.entityResource.data?.selectedEntity;
    const value = form.getFieldValue("Fournisseur_ID");
    if (selected && Number(value?.value ?? value) === Number(selected.id) && value?.label !== selected.name) {
      form.setFieldValue("Fournisseur_ID", { value: Number(selected.id), label: selected.name });
    }
  }, [choices.entityResource.data, form]);
  const profilesFor = (item: any) => normalizePurchasePackagingProfiles(choices.products
    .find((product) => product.id === Number(item.produit_id))?.purchase_packaging_profiles || item.purchase_packaging_profiles);
  const formScope = JSON.stringify([choices.scope.userId, companyContextId, warehouseContextId, effectiveOrderType]);
  const openedScope = useRef(formScope);
  const contextMatches = openedScope.current === formScope;
  useEffect(() => () => { linkedPurchaseOrderRequestRef.current += 1; }, [formScope]);
  useEffect(() => { if (!contextMatches) onClose(); }, [contextMatches, onClose]);
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);
  const [searchKey, setSearchKey] = useState<number>(0);
  const [unitsById, setUnitsById] = useState<
    Record<number, { name?: string; short_name?: string }>
  >({});

  // Logging pour debugging
  useEffect(() => {}, [
    selectedWarehouseId,
    selectedWarehouse,
    selectedCompany,
  ]);

  // Effet pour récupérer les informations complètes du magasin sélectionné
  useEffect(() => {
    let cancelled = false;

    const getWarehouseInfo = async () => {
      setSelectedWarehouse(null);
      if (!warehouseContextId) return;

      message.loading({
        content: "Chargement des informations de l'établissement...",
        key: "warehouseLoading",
        duration: 0,
      });

      try {
        const warehouseInfo = await fetchWarehouseInfo(warehouseContextId);
        if (cancelled) return;
        if (warehouseInfo) {
          setSelectedWarehouse(warehouseInfo);
        } else {
          const minimalWarehouse = {
            id: warehouseContextId,
            name: `Établissement ${warehouseContextId}`,
          };
          setSelectedWarehouse(minimalWarehouse);
        }
      } finally {
        if (!cancelled) {
          message.destroy("warehouseLoading");
        }
      }
    };

    void getWarehouseInfo();
    return () => {
      cancelled = true;
    };
  }, [warehouseContextId]);

  useEffect(() => {
    if (!choices.references) return;
    dispatch({ type: "SET_TAXES", payload: choices.references.taxes || [] });
    setUnitsById(Object.fromEntries((choices.references.units || []).map((unit: any) =>
      [Number(unit.id), { name: unit.name, short_name: unit.short_name }])));
  }, [choices.references, dispatch]);

  // Fonction pour réinitialiser le formulaire
  const resetForm = () => {
    // Réinitialiser le formulaire
    form.resetFields();
    setPurchaseDocument(null);

    // Réinitialiser l'état du formulaire avec les valeurs par défaut
    dispatch({
      type: "SET_FORM_DATA",
      payload: {
        ...initialState.formData,
        Date_Facture: dayjs().format("YYYY-MM-DD"),
        due_date: dayjs().format("YYYY-MM-DD"),
        Statut_Achat: "Commandé",
        produitsAches: [],
        remise_globale: 0,
        purchase_order_id: null,
        Bon_Commande_ID: null,
        document_url: null,
        document_name: null,
      },
    });
  };

  // Fonction pour gérer la fermeture du formulaire
  const handleClose = () => {
    // Réinitialiser le formulaire
    resetForm();

    onReplenishmentCancelled?.();

    // Fermer le formulaire
    onClose();
  };

  const handleCompletedClose = () => {
    resetForm();
    onClose();
    onReplenishmentCompleted?.();
  };

  useEffect(() => {
    if (!visible) return;
    if (state.formData.document_url) {
      setPurchaseDocument({
        url: state.formData.document_url,
        name: state.formData.document_name || "Justificatif d'achat",
        isNew: false,
      });
    } else {
      setPurchaseDocument(null);
    }
  }, [
    state.formData.document_name,
    state.formData.document_url,
    state.formData.id,
    visible,
  ]);

  useEffect(() => {
    let fournisseurValue = undefined;
    if (state.formData.Fournisseur_ID) {
      if (state.formData.supplier_name) {
        fournisseurValue = {
          value: Number(state.formData.Fournisseur_ID),
          label: state.formData.supplier_name,
        };
      } else if (state.fournisseurs && state.fournisseurs.length > 0) {
        const supplier = state.fournisseurs.find(
          (f) => Number(f.id) === Number(state.formData.Fournisseur_ID)
        );
        if (supplier) {
          fournisseurValue = {
            value: Number(state.formData.Fournisseur_ID),
            label:
              supplier.name ||
              supplier.company_name ||
              supplier.Nom_Raison_Sociale ||
              String(state.formData.Fournisseur_ID),
          };
        } else {
          fournisseurValue = {
            value: Number(state.formData.Fournisseur_ID),
            label: String(state.formData.Fournisseur_ID),
          };
        }
      } else {
        fournisseurValue = {
          value: Number(state.formData.Fournisseur_ID),
          label: String(state.formData.Fournisseur_ID),
        };
      }
    }

    form.setFieldsValue({
      ...state.formData,
      Date_Facture: state.formData.Date_Facture
        ? dayjs(state.formData.Date_Facture)
        : dayjs(),
      due_date: state.formData.due_date
        ? dayjs(state.formData.due_date)
        : dayjs(state.formData.Date_Facture || undefined),
      Fournisseur_ID: fournisseurValue,
      Statut_Achat: state.formData.Statut_Achat || "Commandé",
      purchase_order_id:
        state.formData.purchase_order_id ?? state.formData.Bon_Commande_ID,
      // Frais annexes : montants depuis la commande, bascules "inclus" par defaut true
      cout_transport: state.formData.cout_transport,
      cout_assurance: state.formData.cout_assurance,
      cout_douane: state.formData.cout_douane,
      cout_autres_frais: state.formData.cout_autres_frais,
      inclus_transport: state.formData.inclus_transport ?? true,
      inclus_assurance: state.formData.inclus_assurance ?? true,
      inclus_douane: state.formData.inclus_douane ?? true,
      inclus_autres: state.formData.inclus_autres ?? true,
    });
  // Server refreshes only update choices, never fields already being edited.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, state.formData.id, form, companyContextId, warehouseContextId]);

  const calculerTotaux = useCallback(() => {
    const produitsAches = state.formData.produitsAches;
    // Garantir que tous les nombres sont bien des nombres
    const totalHT = produitsAches.reduce(
      (sum, p) =>
        sum +
        toNumberWithDecimalSupport(p.prix_unitaire_HT) *
          toNumberWithDecimalSupport(p.quantite),
      0
    );
    const totalRemises =
      produitsAches.reduce((sum, p) => sum + (Number(p.remise) || 0), 0) +
      (Number(state.formData.remise_globale) || 0);
    const totalTaxes = produitsAches.reduce((sum, p) => {
      if (!p.taxe) return sum;
      const tax = state.taxes.find((t: any) => t.id === p.taxe);
      if (!tax) return sum;
      const montantHT =
        toNumberWithDecimalSupport(p.prix_unitaire_HT) *
          toNumberWithDecimalSupport(p.quantite) -
        (Number(p.remise) || 0);
      return sum + (montantHT * Number(tax.rate || 0)) / 100;
    }, 0);
    const totalTTC = totalHT - totalRemises + totalTaxes;

    // Frais annexes : ceux "inclus" s'ajoutent au du fournisseur ; les autres
    // sont suivis separement (mais tous entrent dans le cout de revient / CMP).
    const fd = state.formData;
    const feeIncl = (amount: any, included?: boolean) =>
      included !== false ? Math.max(0, Number(amount) || 0) : 0;
    const feeSep = (amount: any, included?: boolean) =>
      included === false ? Math.max(0, Number(amount) || 0) : 0;
    const totalFraisInclus =
      feeIncl(fd.cout_transport, fd.inclus_transport) +
      feeIncl(fd.cout_assurance, fd.inclus_assurance) +
      feeIncl(fd.cout_douane, fd.inclus_douane) +
      feeIncl(fd.cout_autres_frais, fd.inclus_autres);
    const totalFraisSepares =
      feeSep(fd.cout_transport, fd.inclus_transport) +
      feeSep(fd.cout_assurance, fd.inclus_assurance) +
      feeSep(fd.cout_douane, fd.inclus_douane) +
      feeSep(fd.cout_autres_frais, fd.inclus_autres);
    const totalDuFournisseur = totalTTC + totalFraisInclus;

    return {
      totalHT,
      totalRemises,
      totalTaxes,
      totalTTC,
      totalFraisInclus,
      totalFraisSepares,
      totalDuFournisseur,
    };
  }, [
    state.formData.produitsAches,
    state.formData.remise_globale,
    state.formData.cout_transport,
    state.formData.cout_assurance,
    state.formData.cout_douane,
    state.formData.cout_autres_frais,
    state.formData.inclus_transport,
    state.formData.inclus_assurance,
    state.formData.inclus_douane,
    state.formData.inclus_autres,
    state.taxes,
  ]);

  const {
    totalHT,
    totalRemises,
    totalTaxes,
    totalTTC,
    totalFraisInclus,
    totalFraisSepares,
    totalDuFournisseur,
  } = calculerTotaux();
  const termsValue = Form.useWatch("termes_conditions", form);
  const remarksValue = Form.useWatch("remarques", form);
  const warehouseContextLabel =
    String(selectedWarehouse?.name || selectedWarehouse?.warehouse_name || "").trim() ||
    (selectedWarehouseId
      ? `Établissement #${(selectedWarehouseId as any)?.id ?? selectedWarehouseId}`
      : "Aucun établissement sélectionné");
  const additionalInformationSummary =
    termsValue || remarksValue
      ? [
          termsValue ? "Termes renseignés" : null,
          remarksValue ? "Remarques renseignées" : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "Termes, conditions et remarques";
  const documentFormTitle = isPurchaseFlow
    ? state.formData.id
      ? "Modifier l’achat"
      : "Nouvel achat"
    : state.formData.id
      ? `Modifier le ${resolvedModuleLabel}`
      : `Nouveau ${resolvedModuleLabel}`;
  const documentSummaryTitle = isPurchaseFlow
    ? "Résumé de l’achat"
    : `Résumé du ${resolvedModuleLabel}`;
  const documentSubmitLabel = isPurchaseFlow
    ? state.formData.id
      ? "Mettre à jour l’achat"
      : "Enregistrer l’achat"
    : state.formData.id
      ? `Mettre à jour le ${resolvedModuleLabel}`
      : `Enregistrer le ${resolvedModuleLabel}`;

  const getProductUnitLabel = (item: any): string => {
    const itemUnitLabel =
      item?.unit_short_name ||
      item?.unit_name ||
      item?.unit?.short_name ||
      item?.unit?.name;
    if (itemUnitLabel && String(itemUnitLabel).trim()) {
      return String(itemUnitLabel).trim();
    }

    const productFromState = state.produits.find(
      (p) => Number(p.id) === Number(item?.produit_id)
    );
    const productUnitLabel =
      productFromState?.unit_short_name ||
      productFromState?.unit_name ||
      productFromState?.unit?.short_name ||
      productFromState?.unit?.name;
    if (productUnitLabel && String(productUnitLabel).trim()) {
      return String(productUnitLabel).trim();
    }

    const unitId =
      item?.unit_id ?? productFromState?.unit_id ?? productFromState?.unit?.id;
    if (unitId !== undefined && unitId !== null) {
      const mappedUnit = unitsById[Number(unitId)];
      if (mappedUnit?.short_name) return mappedUnit.short_name;
      if (mappedUnit?.name) return mappedUnit.name;
    }

    return "unité";
  };

  const resolveProductNameForForm = useCallback(
    (productId: number): string => {
      const product = state.produits.find(
        (p: any) =>
          Number(p?.id) === Number(productId) ||
          Number(p?.id_produit) === Number(productId) ||
          Number(p?.product_id) === Number(productId)
      );

      return (
        product?.name ||
        product?.nom_produit ||
        product?.product_name ||
        `Produit ${productId}`
      );
    },
    [state.produits]
  );

  const mapOrderItemsToFormProducts = useCallback(
    (rawItems: any[] = []): AchatFormData["produitsAches"] => {
      if (!Array.isArray(rawItems)) return [];

      return rawItems
        .map((item: any) => {
          const productId = Number(
            item.product_id ?? item.produit_id ?? item.id_produit
          );
          if (!Number.isFinite(productId) || productId <= 0) {
            return null;
          }

          const productFromState = state.produits.find(
            (p: any) =>
              Number(p?.id) === productId ||
              Number(p?.id_produit) === productId ||
              Number(p?.product_id) === productId
          );

          const quantity = toNumberWithDecimalSupport(
            item.quantity ?? item.quantite ?? 0
          );
          const unitPrice = toNumberWithDecimalSupport(
            item.unit_price ??
              item.prix_unitaire_HT ??
              item.purchase_price ??
              productFromState?.purchase_price ??
              0
          );
          const discountRate = toNumberWithDecimalSupport(
            item.discount_rate ?? item.remise ?? 0
          );

          const rawTaxId = item.tax_id ?? item.taxe;
          const parsedTaxId =
            rawTaxId === null || rawTaxId === undefined || rawTaxId === ""
              ? null
              : Number(rawTaxId);
          const normalizedTaxId =
            parsedTaxId !== null && Number.isFinite(parsedTaxId)
              ? parsedTaxId
              : null;

          const taxRate = toNumberWithDecimalSupport(
            item.tax_rate ??
              (normalizedTaxId !== null
                ? state.taxes.find(
                    (tax: any) => Number(tax?.id) === Number(normalizedTaxId)
                  )?.rate
                : 0) ??
              0
          );
          const computedTax =
            ((unitPrice * quantity - discountRate) * taxRate) / 100;
          const totalTax = toNumberWithDecimalSupport(
            item.total_tax ?? item.montant_taxe ?? computedTax
          );

          const rawUnitId =
            item.unit_id ??
            item.unit?.id ??
            productFromState?.unit_id ??
            productFromState?.unit?.id ??
            null;
          const parsedUnitId =
            rawUnitId === null || rawUnitId === undefined || rawUnitId === ""
              ? null
              : Number(rawUnitId);
          const normalizedUnitId =
            parsedUnitId !== null && Number.isFinite(parsedUnitId)
              ? parsedUnitId
              : null;
          const mappedUnit =
            normalizedUnitId !== null ? unitsById[normalizedUnitId] : undefined;
          const purchasePackagingProfiles = normalizePurchasePackagingProfiles(
            item.purchase_packaging_profiles ||
              productFromState?.purchase_packaging_profiles
          );
          const selectedPurchasePackagingId =
            item.selected_purchase_packaging_id ??
            item.purchase_packaging_id ??
            null;
          const selectedPurchasePackaging =
            purchasePackagingProfiles.find(
              (profile) =>
                selectedPurchasePackagingId !== null &&
                Number(profile.id) === Number(selectedPurchasePackagingId)
            ) || null;
          const purchaseEntryMode =
            selectedPurchasePackaging || item.purchase_entry_mode === "packaging"
              ? "packaging"
              : "direct";

          return {
            id:
              item.id !== null &&
              item.id !== undefined &&
              Number.isFinite(Number(item.id))
                ? Number(item.id)
                : undefined,
            produit_id: productId,
            nom_produit:
              item.product_name ||
              item.nom_produit ||
              resolveProductNameForForm(productId),
            quantite: quantity || 0,
            prix_unitaire_HT: unitPrice,
            remise: discountRate,
            taxe: normalizedTaxId,
            montant_taxe: totalTax,
            unit_id: normalizedUnitId,
            unit_name:
              item.unit_name ||
              item.unit?.name ||
              productFromState?.unit_name ||
              productFromState?.unit?.name ||
              mappedUnit?.name ||
              "",
            unit_short_name:
              item.unit_short_name ||
              item.unit?.short_name ||
              productFromState?.unit_short_name ||
              productFromState?.unit?.short_name ||
              mappedUnit?.short_name ||
              "",
            purchase_packaging_profiles: purchasePackagingProfiles,
            purchase_entry_mode: purchaseEntryMode,
            selected_purchase_packaging_id:
              selectedPurchasePackaging?.id ?? null,
            purchase_packaging_label:
              selectedPurchasePackaging?.label ||
              item.purchase_packaging_label ||
              "",
            purchase_packaging_count:
              selectedPurchasePackaging &&
              selectedPurchasePackaging.contained_quantity > 0
                ? quantity / selectedPurchasePackaging.contained_quantity
                : item.purchase_packaging_count,
          };
        })
        .filter(Boolean) as AchatFormData["produitsAches"];
    },
    [resolveProductNameForForm, state.produits, state.taxes, unitsById]
  );

  const resolveSupplierNameForForm = useCallback(
    (supplierUserId: number, fallbackName?: string): string => {
      const supplier = state.fournisseurs.find(
        (f: any) =>
          Number(f?.user_id) === Number(supplierUserId) ||
          Number(f?.id) === Number(supplierUserId)
      );

      return (
        supplier?.name ||
        supplier?.company_name ||
        supplier?.Nom_Raison_Sociale ||
        fallbackName ||
        `Fournisseur ${supplierUserId}`
      );
    },
    [state.fournisseurs]
  );

  const getCurrentFournisseurValue = () => {
    const currentFournisseur = form.getFieldValue("Fournisseur_ID");

    if (
      currentFournisseur &&
      typeof currentFournisseur === "object" &&
      "value" in currentFournisseur
    ) {
      return currentFournisseur.value;
    }

    return currentFournisseur;
  };

  const updatePurchaseProducts = (
    produitsAches: AchatFormData["produitsAches"]
  ) => {
    dispatch({
      type: "UPDATE_FORM_DATA",
      payload: {
        produitsAches,
        Fournisseur_ID: getCurrentFournisseurValue(),
      },
    });
  };

  const applyPurchasePackagingToProduct = (
    index: number,
    profileId: number | null,
    packageCount?: number | null
  ) => {
    const updatedProducts = [...state.formData.produitsAches];
    const currentProduct = updatedProducts[index];

    if (!currentProduct) {
      return;
    }

    if (profileId === null) {
      updatedProducts[index] = {
        ...currentProduct,
        purchase_entry_mode: "direct",
        selected_purchase_packaging_id: null,
        purchase_packaging_label: "",
        purchase_packaging_count: undefined,
      };
      updatePurchaseProducts(updatedProducts);
      return;
    }

    const currentProfiles = profilesFor(currentProduct);
    const selectedProfile = currentProfiles.find((profile) => Number(profile.id) === Number(profileId));

    if (!selectedProfile) {
      return;
    }

    const inferredPackageCount =
      selectedProfile.contained_quantity > 0
        ? toNumberWithDecimalSupport(currentProduct.quantite) /
          selectedProfile.contained_quantity
        : 1;
    const normalizedPackageCount = normalizePositiveQuantity(
      packageCount && packageCount > 0 ? packageCount : inferredPackageCount,
      1
    );
    const unitPrice =
      selectedProfile.contained_quantity > 0
        ? selectedProfile.reference_purchase_price /
          selectedProfile.contained_quantity
        : 0;

    updatedProducts[index] = {
      ...currentProduct,
      quantite: normalizePositiveQuantity(
        normalizedPackageCount * selectedProfile.contained_quantity
      ),
      prix_unitaire_HT: toNumberWithDecimalSupport(unitPrice.toFixed(4)),
      purchase_entry_mode: "packaging",
      selected_purchase_packaging_id: selectedProfile.id ?? null,
      purchase_packaging_label: selectedProfile.label,
      purchase_packaging_count: normalizedPackageCount,
      purchase_packaging_profiles: currentProfiles,
    };

    updatePurchaseProducts(updatedProducts);
  };

  const handlePurchaseEntryModeChange = (
    index: number,
    nextMode: "direct" | "packaging"
  ) => {
    const updatedProducts = [...state.formData.produitsAches];
    const currentProduct = updatedProducts[index];

    if (!currentProduct) {
      return;
    }

    if (nextMode === "direct") {
      updatedProducts[index] = {
        ...currentProduct,
        purchase_entry_mode: "direct",
        selected_purchase_packaging_id: null,
        purchase_packaging_label: "",
        purchase_packaging_count: undefined,
      };
      updatePurchaseProducts(updatedProducts);
      return;
    }

    const defaultProfile =
      getDefaultPurchasePackagingProfile(
        currentProduct.purchase_packaging_profiles
      ) || null;

    if (!defaultProfile) {
      updatedProducts[index] = {
        ...currentProduct,
        purchase_entry_mode: "direct",
      };
      updatePurchaseProducts(updatedProducts);
      message.warning(
        `Aucun conditionnement d'achat n'est enregistré pour ${productionTerminology.materialDemonstrativeSingular}.`
      );
      return;
    }

    applyPurchasePackagingToProduct(
      index,
      defaultProfile.id ?? null,
      currentProduct.purchase_packaging_count
    );
  };

  const handleLinkedPurchaseOrderChange = useCallback(
    async (value: number | null) => {
      const purchaseOrderId =
        value === null || value === undefined ? null : Number(value);

      dispatch({
        type: "UPDATE_FORM_DATA",
        payload: {
          purchase_order_id: purchaseOrderId,
          Bon_Commande_ID: purchaseOrderId,
        },
      });

      if (!purchaseOrderId || Number.isNaN(purchaseOrderId)) {
        linkedPurchaseOrderRequestRef.current += 1;
        setLoadingLinkedPurchaseOrderItems(false);
        return;
      }

      const requestId = linkedPurchaseOrderRequestRef.current + 1;
      linkedPurchaseOrderRequestRef.current = requestId;
      setLoadingLinkedPurchaseOrderItems(true);
      try {
        const purchaseOrderDetail = await api.fetchAchatDetail(
          String(purchaseOrderId)
        );
        if (requestId !== linkedPurchaseOrderRequestRef.current) {
          return;
        }

        let orderItems = Array.isArray(purchaseOrderDetail?.items)
          ? purchaseOrderDetail.items
          : [];
        if (
          orderItems.length === 0 &&
          Array.isArray(purchaseOrderDetail?.produitsAches)
        ) {
          orderItems = purchaseOrderDetail.produitsAches;
        }
        if (orderItems.length === 0) {
          orderItems = await api.fetchOrderItems(String(purchaseOrderId));
        }
        if (requestId !== linkedPurchaseOrderRequestRef.current) {
          return;
        }

        const mappedProducts = mapOrderItemsToFormProducts(orderItems);
        if (mappedProducts.length === 0) {
          message.warning(
            "Aucun produit trouve dans le bon de commande selectionne."
          );
          return;
        }

        const supplierUserId = Number(purchaseOrderDetail?.user_id ?? 0);
        const nextPayload: Partial<AchatFormData> = {
          produitsAches: mappedProducts,
        };

        if (supplierUserId > 0) {
          const supplierName = resolveSupplierNameForForm(
            supplierUserId,
            purchaseOrderDetail?.supplier_name
          );

          form.setFieldsValue({
            Fournisseur_ID: {
              value: supplierUserId,
              label: supplierName,
            },
          });

          nextPayload.Fournisseur_ID = supplierUserId;
          nextPayload.supplier_name = supplierName;
        }

        if (requestId !== linkedPurchaseOrderRequestRef.current) {
          return;
        }

        dispatch({
          type: "UPDATE_FORM_DATA",
          payload: nextPayload,
        });

        const purchaseOrderNumber =
          purchaseOrderDetail?.invoice_number || `#${purchaseOrderId}`;
        message.success(
          `Produits du bon de commande ${purchaseOrderNumber} charges dans le panier.`
        );
      } catch (error) {
        console.error(
          "Erreur lors du chargement des produits du bon de commande:",
          error
        );
        message.error(
          "Impossible de charger les produits du bon de commande selectionne."
        );
      } finally {
        if (requestId === linkedPurchaseOrderRequestRef.current) {
          setLoadingLinkedPurchaseOrderItems(false);
        }
      }
    },
    [
      api,
      dispatch,
      form,
      mapOrderItemsToFormProducts,
      resolveSupplierNameForForm,
    ]
  );

  const onFinish = async (values: any) => {
    values = { ...form.getFieldsValue(true), ...values };
    if (submittingRef.current || !canSubmitForm || !contextMatches) return;
    submittingRef.current = true;
    try {
      setSubmitting(true);
      const isCreating = !state.formData.id;

      // Extraire l'ID du magasin sélectionné
      let warehouseId = null;
      if (selectedWarehouse) {
        warehouseId = selectedWarehouse.id;
      }

      if (!warehouseId) {
        message.error("Veuillez sélectionner un établissement");
        setSubmitting(false);
        return;
      }

      // Vérifier que selectedCompany est défini
      if (!selectedCompany) {
        console.error("Erreur: Aucune entreprise sélectionnée");
        message.error(
          `Impossible de créer ce ${resolvedModuleLabel}: entreprise non sélectionnée`
        );
        setSubmitting(false);
        return;
      }

      // Extraire l'ID du fournisseur et l'objet fournisseur complet
      let fournisseurId: number | string | null = null;

      // Traiter les différents cas possibles pour Fournisseur_ID
      if (values.Fournisseur_ID) {
        // Cas 1: Fournisseur_ID est un objet avec une propriété value (comme dans le Select avec labelInValue)
        if (
          typeof values.Fournisseur_ID === "object" &&
          values.Fournisseur_ID !== null &&
          "value" in values.Fournisseur_ID
        ) {
          fournisseurId = values.Fournisseur_ID.value;
        }
        // Cas 2: Fournisseur_ID est une valeur directe (nombre ou chaîne)
        else if (
          typeof values.Fournisseur_ID === "number" ||
          typeof values.Fournisseur_ID === "string"
        ) {
          fournisseurId = values.Fournisseur_ID;
        }
      }

      // Si nous n'avons pas trouvé d'ID, essayer de le récupérer depuis state.formData
      if (!fournisseurId && state.formData.Fournisseur_ID) {
        // Traiter les différents cas possibles pour state.formData.Fournisseur_ID
        if (
          typeof state.formData.Fournisseur_ID === "object" &&
          state.formData.Fournisseur_ID !== null &&
          "value" in state.formData.Fournisseur_ID
        ) {
          // Si c'est un objet avec une propriété value
          fournisseurId = state.formData.Fournisseur_ID.value;
        } else if (
          typeof state.formData.Fournisseur_ID === "number" ||
          typeof state.formData.Fournisseur_ID === "string"
        ) {
          // Si c'est une valeur directe
          fournisseurId = state.formData.Fournisseur_ID;
        }
      }

      if (!fournisseurId) {
        message.error("Veuillez sélectionner un fournisseur valide");
        setSubmitting(false);
        return;
      }

      await choices.verify(Number(fournisseurId), state.formData.produitsAches);
      if (isPurchaseFlow && !state.formData.id && immediatePaymentEnabled) {
        const paymentChoices = await initialPaymentChoices.refresh({ force: true });
        if (!paymentChoices.paymentModes.some((mode) => Number(mode.id) === Number(values.initial_payment?.payment_mode_id)) ||
            !paymentChoices.treasuryAccounts.some((account) => Number(account.id) === Number(values.initial_payment?.treasury_account_id))) {
          throw new Error("Un choix du paiement initial n’est plus disponible. Votre saisie est conservée ; corrigez le mode ou le compte.");
        }
      }

      // Log pour le débogage

      const selectedPurchaseOrderIdRaw =
        values.purchase_order_id ??
        state.formData.purchase_order_id ??
        state.formData.Bon_Commande_ID;
      const selectedPurchaseOrderId = selectedPurchaseOrderIdRaw
        ? Number(selectedPurchaseOrderIdRaw)
        : null;
      if (selectedPurchaseOrderId) {
        const confirmedOrders = await purchaseOrders.refresh({ force: true });
        if (Number(confirmedOrders.selectedPurchaseOrder?.id) !== selectedPurchaseOrderId) {
          throw new Error("Le bon de commande sélectionné n’est plus disponible. Votre saisie est conservée.");
        }
      }
      const paidAmount = isPurchaseFlow ? Number(values.paid_amount || 0) : 0;
      const initialPayment =
        isPurchaseFlow && !state.formData.id
          ? buildInitialPaymentPayload(values, {
              paymentType: "out",
              defaultNotes:
                "Paiement enregistré pendant la facturation de l'achat",
            })
          : undefined;
      const normalizedProducts = state.formData.produitsAches.map((item) => {
        const quantity = normalizePositiveQuantity(item.quantite);
        const unitPrice = toNumberWithDecimalSupport(item.prix_unitaire_HT);
        const discountRate = Number(item.remise) || 0;

        return {
          ...item,
          quantite: quantity,
          prix_unitaire_HT: unitPrice,
          remise: discountRate,
        };
      });

      // Préparer les données pour l'API
      const formData = {
        ...values,
        // Ajouter explicitement le warehouse_id
        warehouse_id: warehouseId,
        // Utiliser directement l'ID du fournisseur comme user_id
        user_id: fournisseurId,
        // Supprimer Fournisseur_ID pour éviter la confusion
        Fournisseur_ID: undefined,
        // S'assurer que le statut d'achat est correctement défini
        order_status: values.Statut_Achat || "Commandé",
        // Convertir la date en format ISO
        order_date: values.Date_Facture
          ? values.Date_Facture.format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        due_date:
          isPurchaseFlow && values.due_date
            ? values.due_date.format("YYYY-MM-DD")
            : null,
        document_url: isPurchaseFlow ? purchaseDocument?.url || null : null,
        document_name: isPurchaseFlow ? purchaseDocument?.name || null : null,
        // Supprimer le champ Date_Facture pour éviter la confusion
        Date_Facture: undefined,
        // Ajouter les champs requis par l'API
        order_type: effectiveOrderType,
        invoice_type: null,
        tax_id: null,
        tax_rate: null,
        tax_amount: 0,
        shipping: 0,
        discount: values.remise_globale || 0,
        // Supprimer le champ remise_globale pour éviter la confusion
        remise_globale: undefined,
        subtotal: totalHT,
        // total = TTC marchandise SEULE ; le backend ajoute les frais "inclus".
        total: totalTTC,
        // Frais annexes d'achat (le backend les repartit dans le cout de revient
        // et ajoute les frais "inclus" au total/du fournisseur).
        cout_transport: Math.max(0, Number(values.cout_transport) || 0),
        cout_assurance: Math.max(0, Number(values.cout_assurance) || 0),
        cout_douane: Math.max(0, Number(values.cout_douane) || 0),
        cout_autres_frais: Math.max(0, Number(values.cout_autres_frais) || 0),
        inclus_transport: values.inclus_transport !== false,
        inclus_assurance: values.inclus_assurance !== false,
        inclus_douane: values.inclus_douane !== false,
        inclus_autres: values.inclus_autres !== false,
        // S'assurer que paid_amount est toujours défini à 0 s'il est absent ou null
        paid_amount: paidAmount,
        due_amount: totalDuFournisseur - paidAmount,
        payment_status:
          paidAmount && paidAmount >= totalDuFournisseur
            ? "Payé"
            : paidAmount && paidAmount > 0
            ? "Partiellement payé"
            : "Non payé",
        initial_payment: initialPayment,
        purchase_order_id:
          isPurchaseFlow && selectedPurchaseOrderId
            ? selectedPurchaseOrderId
            : null,
        Bon_Commande_ID: undefined,
        total_items: normalizedProducts.length,
        total_quantity: normalizedProducts.reduce(
          (sum, item) => sum + item.quantite,
          0
        ),
        // Convertir les produits au format API
        items: normalizedProducts.map((item) => ({
          id: item.id ?? null,
          product_id: item.produit_id,
          unit_id: item.unit_id ? Number(item.unit_id) : null,
          quantity: item.quantite,
          unit_price: item.prix_unitaire_HT,
          single_unit_price: item.prix_unitaire_HT,
          tax_id: item.taxe || null,
          tax_rate: item.taxe
            ? state.taxes.find((t) => t.id === item.taxe)?.rate || 0
            : 0,
          tax_type: "percent",
          discount_rate: item.remise,
          total_tax: Number(item.montant_taxe) || 0,
          total_discount:
            (item.prix_unitaire_HT * item.quantite * item.remise) /
              100 || 0,
          subtotal:
            item.prix_unitaire_HT * item.quantite -
            (item.prix_unitaire_HT * item.quantite * item.remise) /
              100,
        })),
        // Convertir les notes et termes en chaînes vides si null/undefined
        notes: values.remarques || "",
        terms_condition: values.termes_conditions || "",
        // Supprimer les champs remarques et termes_conditions pour éviter la confusion
        remarques: undefined,
        termes_conditions: undefined,
        // Ajouter company_id récupéré du contexte
        company_id: companyContextId,
      };

      // Envoyer les données à l'API
      choices.assertContext();
      if (state.formData.id) {
        // Mise à jour d'un achat existant
        await api.updateAchat(state.formData.id, formData, effectiveOrderType);
        message.success(`${resolvedModuleLabelTitle} mis à jour avec succès`);
      } else {
        // Création d'un nouvel achat
        await api.createAchat(formData, effectiveOrderType);
        message.success(`${resolvedModuleLabelTitle} créé avec succès`);
      }

      void choices.invalidate();
      // Rafraîchir la liste des achats et fermer le formulaire
      await refreshAchats();
      if (isCreating && onReplenishmentCompleted) {
        handleCompletedClose();
      } else {
        handleClose();
      }
    } catch (error) {
      console.error(
        `Erreur lors de la soumission du ${resolvedModuleLabel} :`,
        error
      );
      message.error(
        error instanceof Error && error.message
          ? error.message
          : `Une erreur est survenue lors de l'enregistrement du ${resolvedModuleLabel}`
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handlePurchaseDocumentUpload = async (file: File) => {
    setUploadingDocument(true);
    try {
      const response = await api.uploadPurchaseDocument(
        file,
        effectiveOrderType,
        state.formData.id ? "edit" : "create"
      );
      setPurchaseDocument({
        url: response.url,
        name: response.originalName || file.name,
        key: response.key,
        isNew: true,
      });
      message.success("Justificatif ajoute avec succes");
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Impossible de telecharger le justificatif."
      );
    } finally {
      setUploadingDocument(false);
    }
    return false;
  };

  const handlePurchaseDocumentRemove = async () => {
    if (!purchaseDocument) return true;
    try {
      if (purchaseDocument.isNew && purchaseDocument.key) {
        await api.deletePurchaseDocument(
          purchaseDocument.key,
          effectiveOrderType,
          state.formData.id ? "edit" : "create"
        );
      }
      setPurchaseDocument(null);
      return true;
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Impossible de supprimer le justificatif."
      );
      return false;
    }
  };

  const handleAddProduct = (product: Product) => {
    // Vérifier si le produit est déjà dans le panier
    const existingProductIndex = state.formData.produitsAches.findIndex(
      (p) => p.produit_id === product.id
    );

    // Récupérer la valeur actuelle du fournisseur
    const currentFournisseur = form.getFieldValue("Fournisseur_ID");

    // Déterminer la valeur à stocker dans le state
    let fournisseurValue = currentFournisseur;
    if (
      currentFournisseur &&
      typeof currentFournisseur === "object" &&
      "value" in currentFournisseur
    ) {
      fournisseurValue = currentFournisseur.value;
    }

    if (existingProductIndex !== -1) {
      // Si le produit existe déjà, augmenter la quantité
      const updatedProducts = [...state.formData.produitsAches];
      const existingProduct = updatedProducts[existingProductIndex];
      const selectedPackaging = normalizePurchasePackagingProfiles(
        existingProduct.purchase_packaging_profiles
      ).find(
        (profile) =>
          existingProduct.selected_purchase_packaging_id !== null &&
          existingProduct.selected_purchase_packaging_id !== undefined &&
          Number(profile.id) ===
            Number(existingProduct.selected_purchase_packaging_id)
      );

      if (selectedPackaging) {
        const nextPackageCount =
          toNumberWithDecimalSupport(existingProduct.purchase_packaging_count) +
          1;
        updatedProducts[existingProductIndex] = {
          ...existingProduct,
          purchase_packaging_count: nextPackageCount,
          quantite: normalizePositiveQuantity(
            nextPackageCount * selectedPackaging.contained_quantity
          ),
        };
      } else {
        updatedProducts[existingProductIndex].quantite += 1;
      }

      dispatch({
        type: "UPDATE_FORM_DATA",
        payload: {
          produitsAches: updatedProducts,
          Fournisseur_ID: fournisseurValue,
        },
      });
    } else {
      // Sinon, ajouter le produit au panier
      const productFromState = state.produits.find((p) => p.id === product.id);
      const resolvedUnitId =
        product.unit_id ??
        product.unit?.id ??
        productFromState?.unit_id ??
        productFromState?.unit?.id ??
        null;
      const mappedUnit =
        resolvedUnitId !== null ? unitsById[Number(resolvedUnitId)] : undefined;
      const resolvedUnitShortName =
        product.unit_short_name ||
        product.unit?.short_name ||
        productFromState?.unit_short_name ||
        productFromState?.unit?.short_name ||
        mappedUnit?.short_name ||
        "";
      const resolvedUnitName =
        product.unit_name ||
        product.unit?.name ||
        productFromState?.unit_name ||
        productFromState?.unit?.name ||
        mappedUnit?.name ||
        "";
      const purchasePackagingProfiles = normalizePurchasePackagingProfiles(
        product.purchase_packaging_profiles ||
          productFromState?.purchase_packaging_profiles
      );
      const defaultPurchasePackaging = getDefaultPurchasePackagingProfile(
        purchasePackagingProfiles
      );
      const defaultPackageQuantity =
        defaultPurchasePackaging?.contained_quantity || 1;
      const defaultUnitPurchasePrice =
        defaultPurchasePackaging && defaultPurchasePackaging.contained_quantity > 0
          ? defaultPurchasePackaging.reference_purchase_price /
            defaultPurchasePackaging.contained_quantity
          : toNumberWithDecimalSupport(product.purchase_price);

      const newProduct: AchatFormData["produitsAches"][number] = {
        produit_id: product.id,
        nom_produit: product.name,
        quantite: normalizePositiveQuantity(defaultPackageQuantity),
        prix_unitaire_HT: toNumberWithDecimalSupport(defaultUnitPurchasePrice),
        remise: 0,
        taxe: null,
        montant_taxe: 0,
        unit_id: resolvedUnitId,
        unit_name: resolvedUnitName,
        unit_short_name: resolvedUnitShortName,
        purchase_packaging_profiles: purchasePackagingProfiles,
        purchase_entry_mode: defaultPurchasePackaging ? "packaging" : "direct",
        selected_purchase_packaging_id: defaultPurchasePackaging?.id ?? null,
        purchase_packaging_label: defaultPurchasePackaging?.label || "",
        purchase_packaging_count: defaultPurchasePackaging ? 1 : undefined,
      };

      dispatch({
        type: "UPDATE_FORM_DATA",
        payload: {
          produitsAches: [...state.formData.produitsAches, newProduct],
          Fournisseur_ID: fournisseurValue,
        },
      });
    }

    // Réinitialiser la recherche
    setSearchKey((prev) => prev + 1);
  };

  return (
    <>
      <BarcodeScanner
        visible={state.showScanner}
        onClose={() => dispatch({ type: "TOGGLE_SCANNER", payload: false })}
        onScan={(barcode: string) => {}}
      />
      <Modal
        className="sale-form-modal"
        visible={visible}
        title={
          <CommercialDocumentModalTitle
            kind="purchase"
            title={documentFormTitle}
            subtitle={
              isPurchaseFlow
                ? "Sélectionner un fournisseur, ajouter les articles et enregistrer l’achat"
                : "Préparer les articles et enregistrer le bon de commande fournisseur"
            }
            contextLabel={warehouseContextLabel}
          />
        }
        onCancel={handleClose}
        width={window.innerWidth > 768 ? "96%" : "100%"}
        style={{ maxWidth: "1760px" }}
        bodyStyle={{
          maxHeight: "calc(100vh - 108px)",
          overflowY: "auto",
          padding: 0,
        }}
        footer={null}
      >
        <div className="sale-form-modal__content">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={!canSubmitForm || !contextMatches || submitting}
          validateTrigger={["onSubmit"]}
          className="sale-form"
        >
          <div className="sale-form__layout">
            <main className="sale-form__work">
          <BusinessResourceNotice resource={choices.notice} label="les choix de l’achat" />
          {isPurchaseFlow && <BusinessResourceNotice resource={purchaseOrders} label="les bons de commande" />}
          {(choices.missingEntity || choices.missingProducts.length > 0 || choices.missingTaxes.length > 0 || choices.changedPackagings.length > 0) && <Alert type="warning" showIcon
            style={{ marginBottom: 12 }} message="Une référence sélectionnée a changé ou n’est plus disponible."
            description="Votre saisie est conservée. Choisissez un fournisseur disponible ou corrigez les articles concernés avant d’enregistrer." />}
          <Row gutter={[12, 8]} className="sale-form__setup">
            <Col xs={24} md={12}>
              <Form.Item
                name="Fournisseur_ID"
                label="Fournisseur"
                rules={[
                  { required: true, message: "Sélectionnez un fournisseur" },
                ]}
              >
                <Select
                  placeholder="Sélectionner un fournisseur"
                  showSearch
                  labelInValue
                  filterOption={false} onSearch={choices.setSearch} loading={choices.entityResource.loading}
                  dropdownRender={(menu) => <>{menu}<Space style={{ padding: 8 }}>
                    {choices.page > 1 && <Button size="small" onMouseDown={(event) => event.preventDefault()}
                      onClick={() => choices.setPage(choices.page - 1)}>Précédents</Button>}
                    {choices.hasMore && <Button size="small" onMouseDown={(event) => event.preventDefault()}
                      onClick={() => choices.setPage(choices.page + 1)}>Suivants</Button>}
                  </Space></>}
                  onChange={(selected: any, option: any) => {
                    // selected is an object: { value, label }
                    if (!selected) {
                    }

                    const userId = Number(selected.value);

                    if (isNaN(userId) || userId <= 0) {
                      console.error(
                        "ID de fournisseur invalide:",
                        selected.value
                      );
                      message.error("ID de fournisseur invalide");
                      return;
                    }

                    // Trouver le fournisseur complet dans la liste
                    const selectedSupplier = choices.entities.find(
                      (f) => Number(f.id) === userId
                    );

                    // Utiliser le nom du fournisseur trouvé comme étiquette si disponible
                    const supplierName = selectedSupplier
                      ? selectedSupplier.name ||
                        selected.label
                      : selected.label;

                    form.setFieldsValue({
                      Fournisseur_ID: {
                        value: userId,
                        label: supplierName,
                      },
                    });

                    // Mettre à jour state.formData avec l'ID du fournisseur et son nom
                    dispatch({
                      type: "UPDATE_FORM_DATA",
                      payload: {
                        Fournisseur_ID: userId,
                        supplier_name: supplierName, // Stocker le nom du fournisseur pour référence
                      },
                    });
                  }}
                >
                  {choices.entities.map((fournisseur: any) => (
                      <Option
                        key={fournisseur.id}
                        value={Number(fournisseur.id)}
                        searchLabel={buildCommercialEntitySearchText(fournisseur)}
                        title={
                          getCommercialEntityDisplayName(fournisseur, "Nom inconnu")
                        }
                      >
                        {getCommercialEntityDisplayName(
                          fournisseur,
                          "Nom inconnu"
                        )}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={isPurchaseFlow ? 8 : 12} md={isPurchaseFlow ? 4 : 6}>
              <Form.Item
                name="Date_Facture"
                label="Date"
                rules={[{ required: true, message: "Sélectionnez une date" }]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  allowClear={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            {isPurchaseFlow && (
              <Col xs={24} sm={8} md={4}>
                <Form.Item
                  name="due_date"
                  label="Date d'échéance"
                  rules={[
                    {
                      required: true,
                      message: "Sélectionnez une date d'échéance",
                    },
                    {
                      validator: (_, value) => {
                        const purchaseDate = form.getFieldValue("Date_Facture");
                        if (
                          !value ||
                          !purchaseDate ||
                          !dayjs(value).isBefore(dayjs(purchaseDate), "day")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("L'échéance doit être postérieure ou égale à la date d'achat")
                        );
                      },
                    },
                  ]}
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={isPurchaseFlow ? 8 : 12} md={isPurchaseFlow ? 4 : 6}>
              <Form.Item
                name="Statut_Achat"
                label="Statut d'achat"
                rules={[{ required: true, message: "Sélectionnez un statut" }]}
              >
                <Select>
                  <Option value="Commandé">Commandé</Option>
                  <Option value="Reçu">Reçu</Option>
                  <Option value="Annulé">Annulé</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {isPurchaseFlow && (
            <Row gutter={[12, 8]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="purchase_order_id"
                  label="Bon de commande lié (optionnel)"
                >
                  <Select
                    allowClear
                    showSearch
                    loading={
                      loadingPurchaseOrders || loadingLinkedPurchaseOrderItems
                    }
                    placeholder="Sélectionner un bon de commande"
                    filterOption={false} onSearch={setPurchaseOrderSearch}
                    dropdownRender={(menu) => <>{menu}<Space style={{ padding: 8 }}>
                      {purchaseOrderPage > 1 && <Button size="small" onMouseDown={(event) => event.preventDefault()}
                        onClick={() => setPurchaseOrderPage(purchaseOrderPage - 1)}>Précédents</Button>}
                      {purchaseOrders.data?.hasMore && <Button size="small" onMouseDown={(event) => event.preventDefault()}
                        onClick={() => setPurchaseOrderPage(purchaseOrderPage + 1)}>Suivants</Button>}
                    </Space></>}
                    onChange={(value) => {
                      void handleLinkedPurchaseOrderChange(value ?? null);
                    }}
                  >
                    {(purchaseOrders.data?.purchaseOrders || []).map((purchaseOrder: any) => (
                      <Option key={purchaseOrder.id} value={purchaseOrder.id}>
                        {purchaseOrder.invoice_number}
                        {purchaseOrder.supplier_name
                          ? ` - ${purchaseOrder.supplier_name}`
                          : ""}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Pièce justificative (optionnelle)"
                  extra="PDF, JPEG ou PNG — 10 Mo maximum"
                >
                  <Upload
                    accept=".pdf,.jpg,.jpeg,.png"
                    maxCount={1}
                    beforeUpload={handlePurchaseDocumentUpload}
                    onRemove={handlePurchaseDocumentRemove}
                    fileList={
                      purchaseDocument
                        ? [
                            {
                              uid: "purchase-document",
                              name: purchaseDocument.name,
                              status: "done" as const,
                            },
                          ]
                        : []
                    }
                    showUploadList={{ showPreviewIcon: false }}
                  >
                    {!purchaseDocument && (
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingDocument}
                      >
                        {uploadingDocument
                          ? "Téléchargement..."
                          : "Joindre un document"}
                      </Button>
                    )}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={[12, 8]} className="sale-form__composer">
            <Col xs={24} md={20}>
              <Form.Item label="Rechercher un produit">
                <OrderProductSearch
                  key={searchKey}
                  onSelect={(product) => {
                    handleAddProduct({ ...product, product_id: product.product_id ?? undefined });
                  }}
                  orderType={effectiveOrderType} action={state.formData.id ? "edit" : "create"} active={visible}
                  disabled={!canSubmitForm || submitting}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label=" ">
                <Button
                  className="sale-form__scanner"
                  icon={<QrcodeOutlined />}
                  block
                  onClick={() =>
                    dispatch({ type: "TOGGLE_SCANNER", payload: true })
                  }
                >
                  Scanner un code-barres
                </Button>
              </Form.Item>
            </Col>
          </Row>
          <div className="sale-form-table-shell">
            <Table
              className="sale-form-table"
              dataSource={state.formData.produitsAches}
              rowKey={(record: any, index) =>
                record.id
                  ? `order-item-${record.id}`
                  : record.produit_id
                  ? `${record.produit_id}-${index ?? 0}`
                  : (index ?? 0).toString()
              }
              pagination={false}
              size="small"
              scroll={{ x: 1440 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span className="sale-form-table__empty">
                        <strong>Aucun article ajouté</strong>
                        <small>
                          Recherchez un produit ou scannez son code-barres.
                        </small>
                      </span>
                    }
                  />
                ),
              }}
            >
              <Table.Column
                title="Produit"
                dataIndex="nom_produit"
                width={190}
                fixed="left"
              />
              <Table.Column
                title="Mode"
                width={125}
                render={(_: any, record: any, index: number) => {
                  const packagingProfiles = profilesFor(record);

                  return (
                    <Select
                      size="small"
                      value={record.purchase_entry_mode || "direct"}
                      style={{ width: "100%" }}
                      onChange={(nextMode: "direct" | "packaging") =>
                        handlePurchaseEntryModeChange(index, nextMode)
                      }
                    >
                      <Option value="direct">Qté directe</Option>
                      {packagingProfiles.length > 0 && (
                        <Option value="packaging">
                          Conditionnement d'achat
                        </Option>
                      )}
                    </Select>
                  );
                }}
              />
              <Table.Column
                title={compactColumnTitle(
                  "Profil achat",
                  "Profil de conditionnement d'achat utilisé pour convertir le nombre de conditionnements en quantité de stock."
                )}
                width={390}
                render={(_: any, record: any, index: number) => {
                  const packagingProfiles = profilesFor(record);

                  if (record.purchase_entry_mode !== "packaging") {
                    return (
                      <Text type="secondary">
                        Saisie directe en {getProductUnitLabel(record)}
                      </Text>
                    );
                  }

                  if (packagingProfiles.length === 0) {
                    return (
                      <Text type="secondary">
                        Aucun conditionnement enregistré
                      </Text>
                    );
                  }

                  const selectedPackaging = packagingProfiles.find(
                    (profile) =>
                      record.selected_purchase_packaging_id !== null &&
                      record.selected_purchase_packaging_id !== undefined &&
                      Number(profile.id) ===
                        Number(record.selected_purchase_packaging_id)
                  );

                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Select
                        size="small"
                        placeholder="Choisir un profil"
                        value={record.selected_purchase_packaging_id ?? null}
                        style={{ width: 180, flex: "0 0 180px" }}
                        onChange={(profileId?: number | null) => {
                          applyPurchasePackagingToProduct(
                            index,
                            profileId ?? null,
                            record.purchase_packaging_count
                          );
                        }}
                      >
                        {packagingProfiles.map((profile) => (
                          <Option key={profile.id} value={profile.id}>
                            {`${profile.label} - ${formatUnitPrice(
                              profile.contained_quantity
                            )} ${getProductUnitLabel(record)}`}
                          </Option>
                        ))}
                      </Select>
                      {selectedPackaging ? (
                        <>
                          <InputNumber
                            min={MIN_PURCHASE_QUANTITY}
                            step={0.00001}
                            precision={QUANTITY_DECIMAL_PLACES}
                            value={record.purchase_packaging_count ?? 1}
                            addonAfter="cond."
                            style={{ width: 115, flex: "0 0 115px" }}
                            parser={(val: any) =>
                              sanitizeDecimalInput(
                                val,
                                QUANTITY_DECIMAL_PLACES
                              )
                            }
                            onChange={(newValue: any) => {
                              applyPurchasePackagingToProduct(
                                index,
                                selectedPackaging.id ?? null,
                                toNumberWithDecimalSupport(newValue)
                              );
                            }}
                          />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {formatUnitPrice(
                              selectedPackaging.contained_quantity
                            )}{" "}
                            {getProductUnitLabel(record)} / cond.
                          </Text>
                        </>
                      ) : null}
                    </div>
                  );
                }}
              />
              <Table.Column
                title={compactColumnTitle(
                  "PU HT",
                  "Prix unitaire hors taxe dans l'unité de mesure du produit."
                )}
                dataIndex="prix_unitaire_HT"
                width={130}
                render={(value: any, record: any, index: number) => (
                  <InputNumber
                    min={0}
                    step={0.0001}
                    precision={UNIT_PRICE_DECIMAL_PLACES}
                    value={value}
                    addonAfter="CFA"
                    style={{ width: "100%" }}
                    formatter={(val: any) =>
                      val !== undefined && val !== null
                        ? formatUnitPrice(val)
                        : ""
                    }
                    parser={(val: any) => {
                      return sanitizeDecimalInput(
                        val,
                        UNIT_PRICE_DECIMAL_PLACES
                      );
                    }}
                    onChange={(newValue: any) => {
                      const updatedProducts = [...state.formData.produitsAches];
                      // S'assurer que la valeur est toujours un nombre (0 par défaut si undefined ou null)
                      updatedProducts[index].prix_unitaire_HT =
                        toNumberWithDecimalSupport(newValue);
                      const fournisseur = form.getFieldValue("Fournisseur_ID");
                      let fournisseurValue = fournisseur;
                      if (
                        fournisseur &&
                        typeof fournisseur === "object" &&
                        "value" in fournisseur
                      ) {
                        fournisseurValue = fournisseur.value;
                      }
                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          produitsAches: updatedProducts,
                          Fournisseur_ID: fournisseurValue,
                        },
                      });
                    }}
                  />
                )}
              />
              <Table.Column
                title={compactColumnTitle(
                  "Qté",
                  "Quantité achetée dans l'unité de mesure du produit."
                )}
                dataIndex="quantite"
                width={125}
                render={(value: any, record: any, index: number) => (
                  <InputNumber
                    min={MIN_PURCHASE_QUANTITY}
                    step={0.00001}
                    value={value}
                    addonAfter={getProductUnitLabel(record)}
                    style={{ width: "100%" }}
                    parser={(val: any) =>
                      sanitizeDecimalInput(val, QUANTITY_DECIMAL_PLACES)
                    }
                    onChange={(newValue: any) => {
                      const updatedProducts = [...state.formData.produitsAches];
                      // S'assurer que la valeur est toujours un nombre (1 par défaut si undefined ou null)
                      const nextQuantity = normalizePositiveQuantity(newValue);
                      const packagingProfiles = normalizePurchasePackagingProfiles(
                        updatedProducts[index].purchase_packaging_profiles
                      );
                      const selectedPackaging = packagingProfiles.find(
                        (profile) =>
                          updatedProducts[index]
                            .selected_purchase_packaging_id !== null &&
                          updatedProducts[index]
                            .selected_purchase_packaging_id !== undefined &&
                          Number(profile.id) ===
                            Number(
                              updatedProducts[index]
                                .selected_purchase_packaging_id
                            )
                      );

                      updatedProducts[index].quantite = nextQuantity;
                      if (
                        selectedPackaging &&
                        selectedPackaging.contained_quantity > 0
                      ) {
                        updatedProducts[index].purchase_packaging_count =
                          nextQuantity / selectedPackaging.contained_quantity;
                      }

                      // Récupérer la valeur actuelle du fournisseur
                      const currentFournisseur =
                        form.getFieldValue("Fournisseur_ID");
                      let fournisseurValue = currentFournisseur;
                      if (
                        currentFournisseur &&
                        typeof currentFournisseur === "object" &&
                        "value" in currentFournisseur
                      ) {
                        fournisseurValue = currentFournisseur.value;
                      }

                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          produitsAches: updatedProducts,
                          // Préserver l'ID du fournisseur
                          Fournisseur_ID: fournisseurValue,
                        },
                      });
                    }}
                  />
                )}
              />
              <Table.Column
                title={compactColumnTitle("Rem.", "Remise appliquée à la ligne.")}
                dataIndex="remise"
                width={105}
                render={(value: any, record: any, index: number) => (
                  <InputNumber
                    min={0}
                    value={value}
                    addonAfter="CFA"
                    style={{ width: "100%" }}
                    formatter={(val: any) =>
                      val !== undefined && val !== null
                        ? formatNumber(Number(val))
                        : ""
                    }
                    parser={(val: any) => {
                      // Assurer que la valeur est toujours un nombre
                      const parsedValue = val ? val.replace(/\D/g, "") : "0";
                      return parsedValue === "" ? "0" : parsedValue;
                    }}
                    onChange={(newValue: any) => {
                      const updatedProducts = [...state.formData.produitsAches];
                      // S'assurer que la valeur est toujours un nombre (0 par défaut si undefined ou null)
                      updatedProducts[index].remise = Number(newValue || 0);

                      // Récupérer la valeur actuelle du fournisseur
                      const currentFournisseur =
                        form.getFieldValue("Fournisseur_ID");
                      let fournisseurValue = currentFournisseur;
                      if (
                        currentFournisseur &&
                        typeof currentFournisseur === "object" &&
                        "value" in currentFournisseur
                      ) {
                        fournisseurValue = currentFournisseur.value;
                      }

                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          produitsAches: updatedProducts,
                          // Préserver l'ID du fournisseur
                          Fournisseur_ID: fournisseurValue,
                        },
                      });
                    }}
                  />
                )}
              />
              <Table.Column
                title="Taxe"
                dataIndex="taxe"
                width={110}
                render={(value: any, record: any, index: number) => (
                  <Select
                    value={value}
                    style={{ width: "100%" }}
                    placeholder={
                      !selectedCompany
                        ? "Sél. entreprise"
                        : state.taxes.length === 0
                        ? "Aucune taxe"
                        : "Taxe..."
                    }
                    disabled={!selectedCompany || state.taxes.length === 0}
                    onChange={(newValue: any) => {
                      const updatedProducts = [...state.formData.produitsAches];
                      updatedProducts[index].taxe = newValue;

                      // Récupérer la valeur actuelle du fournisseur
                      const currentFournisseur =
                        form.getFieldValue("Fournisseur_ID");
                      let fournisseurValue = currentFournisseur;
                      if (
                        currentFournisseur &&
                        typeof currentFournisseur === "object" &&
                        "value" in currentFournisseur
                      ) {
                        fournisseurValue = currentFournisseur.value;
                      }

                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          produitsAches: updatedProducts,
                          // Préserver l'ID du fournisseur
                          Fournisseur_ID: fournisseurValue,
                        },
                      });
                    }}
                  >
                    <Option value={null}>Aucune</Option>
                    {state.taxes.map((tax: any) => (
                      <Option key={tax.id} value={tax.id}>
                        {tax.name} ({tax.rate}%)
                      </Option>
                    ))}
                  </Select>
                )}
              />
              <Table.Column
                title={compactColumnTitle("Mt taxe", "Montant de taxe calculé.")}
                width={95}
                render={(_: any, record: any) => {
                  const base = record.prix_unitaire_HT * record.quantite;
                  const tax = record.taxe
                    ? state.taxes.find((t: any) => t.id === record.taxe)
                    : null;
                  const montantTaxe = tax ? (base * tax.rate) / 100 : 0;
                  return formatNumber(montantTaxe) + " CFA";
                }}
              />
              <Table.Column
                title="Total TTC"
                width={110}
                render={(_: any, record: any) => {
                  const base = record.prix_unitaire_HT * record.quantite;
                  const rem = record.remise || 0;
                  const tax = record.taxe
                    ? state.taxes.find((t: any) => t.id === record.taxe)
                    : null;
                  const montantTaxe = tax ? (base * tax.rate) / 100 : 0;
                  const total = base - rem + montantTaxe;
                  return formatNumber(total) + " CFA";
                }}
              />
              <Table.Column
                title="Action"
                width={60}
                fixed="right"
                render={(_: any, record: any, index: number) => (
                  <Button
                    className="sale-form-line-action"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Retirer ${record.nom_produit} du ${resolvedModuleLabel}`}
                    onClick={() => {
                      const updatedProducts =
                        state.formData.produitsAches.filter(
                          (_: any, i: number) => i !== index
                        );

                      // Récupérer la valeur actuelle du fournisseur
                      const currentFournisseur =
                        form.getFieldValue("Fournisseur_ID");
                      let fournisseurValue = currentFournisseur;
                      if (
                        currentFournisseur &&
                        typeof currentFournisseur === "object" &&
                        "value" in currentFournisseur
                      ) {
                        fournisseurValue = currentFournisseur.value;
                      }

                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          produitsAches: updatedProducts,
                          // Préserver l'ID du fournisseur
                          Fournisseur_ID: fournisseurValue,
                        },
                      });
                    }}
                  />
                )}
              />
            </Table>
          </div>

          {/* Frais annexes d'achat (transport, assurance, douane, autres) :
              integres au cout de revient (CMP). La bascule "Inclus fournisseur"
              ajoute le frais au total/du fournisseur (sinon suivi separement).
              Uniquement pour les achats (pas les bons de commande). */}
          {isPurchaseFlow && (
          <Card
            className="commercial-document-fees"
            size="small"
            title="Frais annexes (intégrés au coût de revient)"
          >
            <Row gutter={[12, 8]}>
              {[
                { amount: "cout_transport", flag: "inclus_transport", label: "Transport" },
                { amount: "cout_assurance", flag: "inclus_assurance", label: "Assurance" },
                { amount: "cout_douane", flag: "inclus_douane", label: "Douane" },
                { amount: "cout_autres_frais", flag: "inclus_autres", label: "Autres frais" },
              ].map((f) => (
                <Col xs={24} sm={12} md={6} key={f.amount}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <Text>{f.label}</Text>
                    <Form.Item name={f.flag} valuePropName="checked" noStyle>
                      <Switch
                        size="small"
                        checkedChildren="Inclus"
                        unCheckedChildren="Séparé"
                      />
                    </Form.Item>
                  </div>
                  <Form.Item name={f.amount} style={{ marginBottom: 0 }}>
                    <InputNumber
                      min={0}
                      precision={4}
                      style={{ width: "100%" }}
                      placeholder="0"
                      addonAfter="CFA"
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>
          )}

          <CommercialDocumentAdditionalInfo summary={additionalInformationSummary}>
            <Row gutter={[12, 8]}>
              <Col xs={24} md={12}>
                <Form.Item name="termes_conditions" label="Termes et conditions">
                  <Input.TextArea rows={3} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="remarques" label="Remarques">
                  <Input.TextArea rows={3} />
                </Form.Item>
              </Col>
            </Row>
          </CommercialDocumentAdditionalInfo>
            </main>

            <CommercialDocumentSummary
              ariaLabel={documentSummaryTitle}
              title={documentSummaryTitle}
              countLabel={
                state.formData.produitsAches.length === 0
                  ? "Aucun article"
                  : `${state.formData.produitsAches.length} article${
                      state.formData.produitsAches.length > 1 ? "s" : ""
                    }`
              }
              rows={[
                { label: "Base HT", value: `${formatNumber(totalHT)} CFA` },
                { label: "Remises", value: `${formatNumber(totalRemises)} CFA` },
                { label: "Taxes", value: `${formatNumber(totalTaxes)} CFA` },
                {
                  label: "Total TTC marchandises",
                  value: `${formatNumber(totalTTC)} CFA`,
                },
                {
                  label: "Frais inclus",
                  value: `${formatNumber(totalFraisInclus)} CFA`,
                  visible: isPurchaseFlow,
                },
                {
                  label: "Frais suivis séparément",
                  value: `${formatNumber(totalFraisSepares)} CFA`,
                  visible: isPurchaseFlow && totalFraisSepares > 0,
                },
              ]}
              totalLabel={
                isPurchaseFlow
                  ? "Total dû au fournisseur"
                  : "Total du bon de commande"
              }
              totalValue={`${formatNumber(
                isPurchaseFlow ? totalDuFournisseur : totalTTC,
              )} CFA`}
              actions={
                <>
                  <Button onClick={handleClose}>Annuler</Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={submitting}
                    disabled={!canSubmitForm || !contextMatches || uploadingDocument || !choices.ready || choices.missingEntity || choices.missingProducts.length > 0 || choices.missingTaxes.length > 0 || choices.changedPackagings.length > 0}
                  >
                    {documentSubmitLabel}
                  </Button>
                </>
              }
            >
              {isPurchaseFlow && !state.formData.id ? (
                <OrderInitialPaymentFields
                  form={form}
                  total={totalDuFournisseur}
                  companyId={selectedCompany}
                  warehouseId={selectedWarehouse?.id ?? selectedWarehouseId}
                  canCreatePayment={canCreateInitialPayment}
                  choiceResource={initialPaymentChoices}
                  presentation="summary"
                  inactiveTitle="Achat non réglé"
                  sectionTitle="Règlement fournisseur"
                  inactiveOptionLabel="Sans règlement"
                  activeOptionLabel="Ajouter un règlement"
                  description="Règlement fournisseur partiel ou total, enregistré avec l’achat."
                />
              ) : (
                <div className="sale-form-summary__payment-readonly">
                  <span>Paiement</span>
                  <Tag color={isPurchaseFlow ? "orange" : "blue"}>
                    {isPurchaseFlow
                      ? (state.formData as any).payment_status || "Géré depuis le détail"
                      : "Non applicable"}
                  </Tag>
                </div>
              )}
            </CommercialDocumentSummary>
          </div>
        </Form>
        </div>
      </Modal>
    </>
  );
};

/* --- Tableau principal des achats --- */
const AchatsTable: React.FC<AchatsTableProps> = ({
  refreshAchats,
  orderType = "purchase",
  permissionRoot,
  moduleLabel,
  moduleLabelPlural,
  showPayments = true,
}) => {
  const invalidatePurchases = useBusinessMutationInvalidation("purchase");
  const context = useContext(AchatsContext);
  if (!context) {
    throw new Error("AchatsTable must be used within a AchatsContext.Provider");
  }
  const { state, dispatch, api } = context;
  const effectiveOrderType = normalizePurchaseFlowOrderType(orderType);
  const flowLabels = getFlowLabels(effectiveOrderType);
  const resolvedModuleLabel = moduleLabel || flowLabels.singular;
  const resolvedModuleLabelPlural = moduleLabelPlural || flowLabels.plural;
  const resolvedModuleLabelTitle = capitalizeLabel(resolvedModuleLabel);
  const resolvedPermissionRoot =
    permissionRoot || ORDER_TYPE_PERMISSION_ROOT_MAP[effectiveOrderType];
  const showPaymentColumns =
    showPayments && effectiveOrderType === "purchase";
  const { selectedWarehouse, selectedCompany } = useSelection();
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [detailOrder, setDetailOrder] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [unitsById, setUnitsById] = useState<
    Record<number, { name?: string; short_name?: string }>
  >({});
  // @ts-ignore - Context from JS file
  const { user, hasPermission } = useAuth(); // Get user object from AuthContext

  // Logging pour debugging
  useEffect(() => {}, [selectedWarehouse, selectedCompany]);

  useEffect(() => {
    const loadUnits = async () => {
      try {
        const selectedCompanyId =
          (selectedCompany as any)?.id ?? selectedCompany;
        const parsedCompanyId = selectedCompanyId
          ? Number(selectedCompanyId)
          : undefined;
        const companyId =
          parsedCompanyId && !Number.isNaN(parsedCompanyId)
            ? parsedCompanyId
            : undefined;

        const units = await api.fetchUnits(companyId);
        const nextUnitsById = (Array.isArray(units) ? units : []).reduce(
          (
            acc: Record<number, { name?: string; short_name?: string }>,
            unit: any
          ) => {
            const unitId = Number(unit?.id);
            if (!Number.isNaN(unitId)) {
              acc[unitId] = {
                name: unit?.name,
                short_name: unit?.short_name,
              };
            }
            return acc;
          },
          {}
        );

        setUnitsById(nextUnitsById);
      } catch (error) {
        console.error("Erreur lors du chargement des unités (table achats):", error);
        setUnitsById({});
      }
    };

    loadUnits();
  }, [api, selectedCompany]);

  const findProductById = useCallback(
    (productId: any) =>
      state.produits.find(
        (p: any) =>
          Number(p?.id) === Number(productId) ||
          Number(p?.id_produit) === Number(productId) ||
          Number(p?.product_id) === Number(productId)
      ),
    [state.produits]
  );

  const resolveProductName = useCallback(
    (productId: any): string => {
      const product = findProductById(productId);
      return (
        product?.name ||
        product?.nom_produit ||
        product?.product_name ||
        `Produit ${productId}`
      );
    },
    [findProductById]
  );

  // Nouvelle fonction pour charger les détails pour le formulaire de modification
  const loadAchatForEdit = async (record: any) => {
    try {
      setLoading(true);

      // Récupérer les détails de l'achat
      const achatData = await api.fetchAchatDetail(String(record.id));

      // Vérifier si les produitsAches sont déjà inclus dans la réponse
      let produitsAches = [];

      if (achatData.produitsAches && achatData.produitsAches.length > 0) {
        // Mapper les éléments de l'achat au format attendu par le formulaire
        produitsAches = achatData.produitsAches.map((item: any) => ({
          id:
            item.id !== null &&
            item.id !== undefined &&
            Number.isFinite(Number(item.id))
              ? Number(item.id)
              : undefined,
          produit_id: item.product_id,
          nom_produit:
            item.product_name ||
            resolveProductName(item.product_id),
          quantite: normalizePositiveQuantity(item.quantity, 0),
          prix_unitaire_HT: toNumberWithDecimalSupport(item.unit_price),
          remise: Number(item.discount_rate) || 0,
          taxe: item.tax_id,
          montant_taxe: Number(item.total_tax) || 0,
          unit_id: item.unit_id ?? null,
          unit_name: item.unit_name || item.unit?.name || "",
          unit_short_name:
            item.unit_short_name || item.unit?.short_name || "",
        }));
      } else {
        // Si les produitsAches ne sont pas inclus, récupérer les éléments de l'achat séparément
        const orderItems = await api.fetchOrderItems(record.id);

        if (orderItems && orderItems.length > 0) {
          // Mapper les éléments de l'achat au format attendu par le formulaire
          produitsAches = orderItems.map((item: any) => ({
            id:
              item.id !== null &&
              item.id !== undefined &&
              Number.isFinite(Number(item.id))
                ? Number(item.id)
                : undefined,
            produit_id: item.product_id,
            nom_produit:
              item.product_name ||
              resolveProductName(item.product_id),
            quantite: normalizePositiveQuantity(item.quantity, 0),
            prix_unitaire_HT: toNumberWithDecimalSupport(item.unit_price),
            remise: Number(item.discount_rate) || 0,
            taxe: item.tax_id,
            montant_taxe: Number(item.total_tax) || 0,
            unit_id: item.unit_id ?? null,
            unit_name: item.unit_name || item.unit?.name || "",
            unit_short_name:
              item.unit_short_name || item.unit?.short_name || "",
          }));
        }
      }

      // Préparer les données du formulaire
      const formattedData: AchatFormData = {
        id: achatData.id,
        Date_Facture: achatData.order_date || dayjs().format("YYYY-MM-DD"),
        due_date: achatData.due_date || null,
        Statut_Achat: achatData.order_status || "Commandé",
        Fournisseur_ID: achatData.user_id,
        warehouse_id: achatData.warehouse_id,
        remise_globale: achatData.discount || 0,
        termes_conditions: achatData.terms_condition || "",
        remarques: achatData.notes || "",
        paid_amount: achatData.paid_amount || 0,
        supplier_name: achatData.supplier_name,
        purchase_order_id:
          achatData.purchase_order_id ?? achatData.Bon_Commande_ID ?? null,
        Bon_Commande_ID:
          achatData.purchase_order_id ?? achatData.Bon_Commande_ID ?? null,
        document_url: achatData.document_url || null,
        document_name: achatData.document_name || null,
        // Frais annexes d'achat (depuis les colonnes extra_cost_* de la commande)
        cout_transport: Number(achatData.extra_cost_transport) || 0,
        cout_assurance: Number(achatData.extra_cost_assurance) || 0,
        cout_douane: Number(achatData.extra_cost_douane) || 0,
        cout_autres_frais: Number(achatData.extra_cost_autres) || 0,
        inclus_transport: Number(achatData.extra_cost_transport_included) !== 0,
        inclus_assurance: Number(achatData.extra_cost_assurance_included) !== 0,
        inclus_douane: Number(achatData.extra_cost_douane_included) !== 0,
        inclus_autres: Number(achatData.extra_cost_autres_included) !== 0,
        produitsAches: produitsAches,
      };

      // Trouver le fournisseur correspondant pour obtenir son nom
      const selectedSupplier = state.fournisseurs.find(
        (f) => f.user_id === achatData.user_id
      );

      if (selectedSupplier) {
        const supplierName =
          selectedSupplier.name ||
          selectedSupplier.company_name ||
          selectedSupplier.Nom_Raison_Sociale ||
          achatData.supplier_name ||
          "Fournisseur " + achatData.user_id;

        // Mettre à jour le nom du fournisseur dans les données formatées
        formattedData.supplier_name = supplierName;
      } else {
        console.warn("Fournisseur non trouvé pour l'ID:", achatData.user_id);
      }

      if (formattedData.produitsAches.length === 0) {
        message.warning(
          "Aucun produit trouvé dans cette commande. Veuillez vérifier dans la base de données si la table order_items contient des entrées pour cet enregistrement (ID: " +
            record.id +
            ")."
        );
      }

      // Mettre à jour l'état avec les données du formulaire et ouvrir le formulaire
      dispatch({ type: "SET_FORM_DATA", payload: formattedData });
      dispatch({ type: "TOGGLE_FORM", payload: true });
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails de la commande pour modification:",
        error
      );
      message.error("Erreur lors du chargement des détails");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger les détails spécifiquement pour le modal de détail
  const loadAchatDetailsForModal = async (record: any) => {
    try {
      setLoading(true);

      // Récupérer les détails COMPLETS de l'achat (incluant items et payments)
      const achatDetail = await api.fetchAchatDetail(record.id);

      // Créer une copie de l'enregistrement pour le mettre à jour
      const updatedRecord = { ...record }; // Start with base record info

      // Frais annexes (transport, assurance, douane, autres) : recopier depuis le
      // detail complet (GET /api/orders/:id renvoie SELECT o.*) pour garantir leur
      // presence dans le modal, meme si la ligne de liste ne les contient pas.
      updatedRecord.extra_cost_transport = achatDetail.extra_cost_transport;
      updatedRecord.extra_cost_assurance = achatDetail.extra_cost_assurance;
      updatedRecord.extra_cost_douane = achatDetail.extra_cost_douane;
      updatedRecord.extra_cost_autres = achatDetail.extra_cost_autres;
      updatedRecord.extra_cost_transport_included =
        achatDetail.extra_cost_transport_included;
      updatedRecord.extra_cost_assurance_included =
        achatDetail.extra_cost_assurance_included;
      updatedRecord.extra_cost_douane_included =
        achatDetail.extra_cost_douane_included;
      updatedRecord.extra_cost_autres_included =
        achatDetail.extra_cost_autres_included;
      updatedRecord.extra_cost_total = achatDetail.extra_cost_total;
      updatedRecord.extra_cost_included_total =
        achatDetail.extra_cost_included_total;
      updatedRecord.due_date = achatDetail.due_date;
      updatedRecord.document_url = achatDetail.document_url;
      updatedRecord.document_name = achatDetail.document_name;

      // --- Gestion des Paiements ---
      if (achatDetail.payments && Array.isArray(achatDetail.payments)) {
        updatedRecord.payments = achatDetail.payments;
      } else {
        console.warn(
          `Aucun paiement trouvé dans achatDetail pour la commande ${record.id}. L'API /api/orders/:id devrait idéalement retourner les paiements.`
        );
        // Tentative de chargement séparé (fallback, peut être retiré si l'API /api/orders/:id est fiable)
        try {
          const response = await fetch(
            `${BASE_URL}/api/orders/${record.id}/payments`, // Ensure this endpoint exists and works
            {
              headers: getAuthHeaders(),
            }
          );
          if (!response.ok) {
            throw new Error(
              `Erreur ${response.status}: ${response.statusText}`
            );
          }
          const payments = await response.json();

          updatedRecord.payments = payments || []; // Ensure it's an array
        } catch (error) {
          updatedRecord.payments = []; // Set empty array on error
          message.error("Erreur lors du chargement des paiements associés.");
        }
      }

      // --- Gestion des Produits/Items ---
      let orderItemsData = [];
      // Prioriser achatDetail.items
      if (
        achatDetail.items &&
        Array.isArray(achatDetail.items) &&
        achatDetail.items.length > 0
      ) {
        // Mapper pour assurer la présence de product_name si possible
        orderItemsData = achatDetail.items.map((item: any) => ({
          ...item, // Keep original item data
          product_name:
            item.product_name || // Use name from backend if available
            resolveProductName(item.product_id), // Fallback to global list
        }));
      }
      // Fallback: Vérifier achatDetail.produitsAches (ancienne structure?)
      else if (
        achatDetail.produitsAches &&
        Array.isArray(achatDetail.produitsAches) &&
        achatDetail.produitsAches.length > 0
      ) {
        console.warn(
          "Utilisation de 'produitsAches' trouvé dans achatDetail (préférer 'items'):",
          achatDetail.produitsAches.length
        );
        orderItemsData = achatDetail.produitsAches.map((item: any) => ({
          ...item,
          // Ensure standard fields expected by the modal table
          product_id: item.product_id || item.produit_id,
          quantity: item.quantity || item.quantite,
          unit_price: item.unit_price || item.prix_unitaire_HT,
          discount_rate: item.discount_rate || item.remise || 0,
          tax_id: item.tax_id || item.taxe,
          subtotal: item.subtotal, // Assuming subtotal is present
          product_name:
            item.product_name ||
            item.nom_produit ||
            resolveProductName(item.product_id || item.produit_id),
        }));
      }
      // Fallback: Appeler api.fetchOrderItems si ni items ni produitsAches ne sont présents
      else {
        console.warn(
          `Aucun item/produit trouvé dans achatDetail pour la commande ${record.id}. Tentative de récupération via api.fetchOrderItems.`
        );
        try {
          const fetchedItems = await api.fetchOrderItems(record.id);

          if (
            fetchedItems &&
            Array.isArray(fetchedItems) &&
            fetchedItems.length > 0
          ) {
            // Mapper les éléments au format attendu, ajoutant product_name
            orderItemsData = fetchedItems.map((item: any) => ({
              ...item, // Keep original item data
              product_name:
                item.product_name || // Use name from backend if available
                resolveProductName(item.product_id), // Fallback to global list
            }));
          } else {
            console.warn(
              "Aucun produit trouvé pour l'achat ID (modal) via api.fetchOrderItems:",
              record.id
            );
            message.warning(
              `Aucun produit trouvé pour la commande #${record.invoice_number}`
            );
          }
        } catch (fetchError) {
          console.error(
            "Erreur lors de la récupération des items via api.fetchOrderItems:",
            fetchError
          );
          message.error(
            `Erreur lors du chargement des produits pour la commande #${record.invoice_number}`
          );
        }
      }

      // Mettre à jour l'enregistrement avec les items et paiements
      updatedRecord.items = orderItemsData; // Utiliser la clé 'items'
      // updatedRecord.produitsAches = orderItemsData; // Peut être retiré si 'produitsAches' n'est plus utilisé

      // Mettre à jour l'état du détail de la commande pour le modal
      setDetailOrder(updatedRecord);

      return updatedRecord;
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails pour le modal:",
        error
      );
      message.error("Erreur lors du chargement des détails");
      setDetailOrder(null); // Clear order on error
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (record: any) => {
    const endpoint =
      effectiveOrderType === "purchase_order"
        ? `/api/orders/${record.id}/purchase-order-pdf?download=true`
        : `/api/orders/${record.id}/invoice?download=true`;

    try {
      message.loading({ content: "Génération du PDF...", key: "orderPdf" });
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || response.statusText);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${record.invoice_number || record.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      message.success({ content: "PDF téléchargé avec succès", key: "orderPdf" });
    } catch (error) {
      console.error("Erreur lors du téléchargement du document:", error);
      message.error({
        content: "Impossible de télécharger le document.",
        key: "orderPdf",
      });
    }
  };

  const columns: any[] = [
    {
      title: "Numéro de saisi",
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 165,
      render: (value: string) => (
        <span className="commercial-list__number">{value}</span>
      ),
    },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (text: string): string => dayjs(text).format("DD/MM/YYYY"),
      sorter: (a: any, b: any) => {
        const dateA = dayjs(a.order_date);
        const dateB = dayjs(b.order_date);
        return dateA.valueOf() - dateB.valueOf();
      },
      defaultSortOrder: "descend" as const,
      width: 110,
    },
    {
      title: "Fournisseur",
      key: "fournisseur",
      render: (_: any, record: any): React.ReactNode => {
        if (!state.fournisseurs || state.fournisseurs.length === 0) {
          return "Chargement...";
        }

        if (!record.user_id) {
          return "Non spécifié";
        }

        const supplier = state.fournisseurs.find(
          (f: any) => Number(f.id) === Number(record.user_id)
        );

        if (supplier) {
          const supplierName =
            supplier.name ||
            supplier.company_name ||
            supplier.Nom_Raison_Sociale;
          return supplierName || "Nom inconnu";
        }

        const alternativeSupplier = state.fournisseurs.find(
          (f: any) =>
            (f.ID_Frs && Number(f.ID_Frs) === Number(record.user_id)) ||
            (f.user_id && Number(f.user_id) === Number(record.user_id))
        );

        if (alternativeSupplier) {
          const supplierName =
            alternativeSupplier.name ||
            alternativeSupplier.company_name ||
            alternativeSupplier.Nom_Raison_Sociale;
          return supplierName || "Nom inconnu";
        }

        if (record.supplier_name || record.user_name || record.entity_name) {
          return record.supplier_name || record.user_name || record.entity_name;
        }

        return "Inconnu";
      },
      width: 190,
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      width: 140,
    },
    {
      title: "Statut",
      dataIndex: "order_status",
      key: "order_status",
      render: (status: string) => {
        let color = "green";
        if (status === "En attente") color = "orange";
        if (status === "Annulé") color = "red";
        return <Tag color={color}>{status}</Tag>;
      },
      width: 110,
    },
  ];

  if (effectiveOrderType === "purchase") {
    columns.push({
      title: "Bon de commande",
      dataIndex: "purchase_order_invoice_number",
      key: "purchase_order_invoice_number",
      render: (value: string) => value || "-",
      width: 150,
    });
  }

  if (showPaymentColumns) {
    columns.push({
      title: "Statut paiement",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status: string) => {
        let color = "";
        let displayText = status;

        if (status === "Non payé") {
          color = "red";
          displayText = "Non payé";
        } else if (status === "Partiel" || status === "Partiellement payé") {
          color = "orange";
          displayText = "Partiellement payé";
        } else if (status === "Payé") {
          color = "green";
          displayText = "Payé";
        }

        return <Tag color={color}>{displayText}</Tag>;
      },
      width: 135,
    });
    columns.push({
      title: "Montant payé",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right",
      render: (amount: number) => `${formatTotalAmount(amount || 0)} CFA`,
      width: 125,
    });
  } else if (effectiveOrderType === "purchase_order") {
    columns.push({
      title: "Achats liés",
      dataIndex: "linked_purchase_count",
      key: "linked_purchase_count",
      render: (value: number) => Number(value || 0),
      width: 100,
    });
  }

  columns.push({
    title: "Total",
    dataIndex: "total",
    key: "total",
    align: "right",
    render: (total: number) => `${formatTotalAmount(total)} CFA`,
    width: 125,
  });

  columns.push({
    title: "Actions",
    key: "actions",
    width: 58,
    align: "center",
    render: (_: any, record: any) => {
      const canView = hasPermission(`${resolvedPermissionRoot}.view`);
      const canEdit = hasPermission(`${resolvedPermissionRoot}.edit`);
      const canDelete = hasPermission(`${resolvedPermissionRoot}.delete`);
      const canDownload =
        hasPermission(`${resolvedPermissionRoot}.generate_pdf`) || canView;
      const isDeleted = record.is_deleted === 1;
      const hasRegisteredPayments =
        showPaymentColumns && Number(record.paid_amount) > 0;
      const editDisabled = isDeleted || hasRegisteredPayments;
      const actionItems: any[] = [];

      if (canView) {
        actionItems.push({
          key: "view",
          icon: <EyeOutlined />,
          label: "Voir le détail",
          onClick: async () => {
            setLoading(true);
            try {
              const fullDetail = await loadAchatDetailsForModal(record);
              if (fullDetail) {
                setDetailOrder(fullDetail);
                setShowDetailModal(true);
              }
            } catch (err) {
              console.error("Erreur lors du chargement du détail:", err);
              message.error(
                `Impossible de charger le détail de ce ${resolvedModuleLabel}.`
              );
            } finally {
              setLoading(false);
            }
          },
        });
      }

      if (canDownload) {
        actionItems.push({
          key: "download",
          icon: <DownloadOutlined />,
          label: "Télécharger le PDF",
          onClick: () => handleDownloadDocument(record),
        });
      }

      if (canEdit) {
        actionItems.push({
          key: "edit",
          icon: <EditOutlined />,
          label: editDisabled ? "Modification indisponible" : "Modifier",
          disabled: editDisabled,
          onClick: () => loadAchatForEdit(record),
        });
      }

      if (canDelete) {
        if (actionItems.length > 0) {
          actionItems.push({ type: "divider" });
        }

        if (isDeleted) {
          actionItems.push({
            key: "restore",
            icon: <UndoOutlined />,
            label: "Restaurer",
            onClick: () =>
              Modal.confirm({
                title: "Confirmation",
                content: `Voulez-vous restaurer ce ${resolvedModuleLabel} ?`,
                onOk: async () => {
                  try {
                    await api.restoreAchat(record.id);
                    void invalidatePurchases();
                    await refreshAchats();
                    message.success(
                      `${resolvedModuleLabelTitle} restauré avec succès`
                    );
                  } catch (error) {
                    message.error(
                      error instanceof Error
                        ? error.message
                        : "Erreur lors de la restauration"
                    );
                  }
                },
              }),
          });
        } else if (hasRegisteredPayments) {
          actionItems.push({
            key: "paid-correction-required",
            icon: <ExclamationCircleOutlined />,
            label: "Avoir ou retour requis (paiement enregistré)",
            disabled: true,
            onClick: () => undefined,
          });
        } else {
          const deleteDisabled = record.is_deletable === 0;
          actionItems.push({
            key: "delete",
            icon: <DeleteOutlined />,
            label: deleteDisabled ? "Suppression indisponible" : "Supprimer",
            disabled: deleteDisabled,
            danger: !deleteDisabled,
            onClick: () =>
              Modal.confirm({
                title: "Confirmation",
                content: `Voulez-vous vraiment supprimer ce ${resolvedModuleLabel} ?`,
                onOk: async () => {
                  try {
                    await api.deleteAchat(record.id);
                    void invalidatePurchases();
                    dispatch({
                      type: "DELETE_ACHAT",
                      payload: record.id,
                    });
                    message.success(
                      `${resolvedModuleLabelTitle} supprimé avec succès`
                    );
                  } catch (error) {
                    message.error(
                      error instanceof Error
                        ? error.message
                        : "Erreur lors de la suppression"
                    );
                  }
                },
              }),
          });
        }
      }

      return (
        <RowActionsMenu
          items={actionItems}
          loading={loading}
          ariaLabel={`Afficher les actions du ${resolvedModuleLabel} ${
            record.invoice_number || record.id
          }`}
        />
      );
    },
  });

  const expandable = {
    columnWidth: 44,
    expandedRowRender: (record: any) => {
      // Render the new component, passing the order ID
      return <ExpandedRowContent orderId={record.id} unitsById={unitsById} />;
    },
    onExpand: (expanded: boolean, record: any) => {
      // Only manage the expanded row keys state here
      if (expanded) {
        setExpandedRowKeys((prev) => [...prev, record.id]);
        // REMOVED: loadAchatDetails(record);
      } else {
        setExpandedRowKeys((prev) => prev.filter((key) => key !== record.id));
      }
    },
    expandedRowKeys,
  };

  // Ajout d'un useEffect pour déboguer le mappage entre les achats et les fournisseurs
  useEffect(() => {
    if (state.achats.length > 0 && state.fournisseurs.length > 0) {
      // Analyser les IDs des fournisseurs présents dans les achats
      const supplierIdsInOrders = new Set(
        state.achats.map((achat) => achat.user_id).filter(Boolean)
      );

      // Analyser les IDs disponibles dans la liste des fournisseurs
      const availableSupplierIds = new Set(
        state.fournisseurs.map((f: any) => f.id)
      );

      // Trouver les IDs qui sont dans les achats mais pas dans la liste des fournisseurs
      const missingSupplierIds = Array.from(supplierIdsInOrders).filter(
        (id) => !availableSupplierIds.has(id)
      );
      if (missingSupplierIds.length > 0) {
      } else {
      }
    }
  }, [state.achats, state.fournisseurs]);

  if (state.loading) return <Spin size="large" />;
  if (state.error) return <div>{state.error}</div>;

  return (
    <>
      <div className="commercial-list__table-wrap">
        <Table
          className="commercial-list__table"
          dataSource={state.achats}
          columns={columns}
          rowKey="id"
          size="middle"
          expandable={expandable}
          pagination={{
            current: state.currentPage,
            pageSize: state.itemsPerPage,
            total: state.totalPages * state.itemsPerPage,
            onChange: (page, pageSize) => {
              if (pageSize !== state.itemsPerPage) {
                dispatch({ type: "SET_ITEMS_PER_PAGE", payload: pageSize });
              } else {
                dispatch({ type: "SET_PAGE", payload: page });
              }
            },
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} ${resolvedModuleLabelPlural}`,
            pageSizeOptions: ["10", "20", "50", "100"],
            position: ["bottomRight"],
            responsive: true,
          }}
          sortDirections={["descend", "ascend"]}
          scroll={{ x: 1360 }}
        />
      </div>

      {detailOrder && (
        <OrderDetailModal
          order={detailOrder}
          visible={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setDetailOrder(null);
          }}
          produits={state.produits}
          taxes={state.taxes}
          fournisseurs={state.fournisseurs}
          orderType={effectiveOrderType}
          showPayments={showPaymentColumns}
          moduleLabel={resolvedModuleLabel}
          moduleLabelPlural={resolvedModuleLabelPlural}
          refreshOrderDetails={(orderId) =>
            loadAchatDetailsForModal({ id: orderId })
          }
          refreshAchats={refreshAchats}
        />
      )}
    </>
  );
};

// Internal component to fetch and display expanded row details
const ExpandedRowContent: React.FC<{
  orderId: string;
  unitsById?: Record<number, { name?: string; short_name?: string }>;
}> = ({ orderId, unitsById = {} }) => {
  const [items, setItems] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const { state: globalState, api } = useContext(AchatsContext)!; // Assuming context is always available here

  useEffect(() => {
    const fetchDetails = async () => {
      setLoadingDetails(true);
      try {
        // Use fetchAchatDetail as it includes items and potentially product names
        const achatDetail = await api.fetchAchatDetail(orderId);

        let fetchedItems = [];
        if (
          achatDetail &&
          achatDetail.items &&
          Array.isArray(achatDetail.items)
        ) {
          fetchedItems = achatDetail.items.map((item: any) => {
            const resolvedProductId = item?.product_id ?? item?.produit_id;
            const productFromState = globalState.produits.find(
              (p: any) =>
                Number(p?.id) === Number(resolvedProductId) ||
                Number(p?.id_produit) === Number(resolvedProductId) ||
                Number(p?.product_id) === Number(resolvedProductId)
            );
            const unitId =
              item?.unit_id ??
              item?.unit?.id ??
              productFromState?.unit_id ??
              productFromState?.unit?.id;
            const mappedUnit =
              unitId !== undefined && unitId !== null
                ? unitsById[Number(unitId)]
                : undefined;
            const unitLabel =
              item.unit_short_name ||
              item.unit_name ||
              item.unit?.short_name ||
              item.unit?.name ||
              productFromState?.unit_short_name ||
              productFromState?.unit_name ||
              productFromState?.unit?.short_name ||
              productFromState?.unit?.name ||
              mappedUnit?.short_name ||
              mappedUnit?.name;

            return {
              ...item, // Spread original item data
              id: item.id || `${orderId}-${resolvedProductId}`, // Ensure a unique key
              product_id: resolvedProductId,
              nom_produit:
                item.product_name || // Use name from backend if available
                productFromState?.name ||
                productFromState?.nom_produit ||
                productFromState?.product_name || // Fallback to global product list
                "Produit inconnu", // Final fallback
              quantite: item.quantity,
              unit_label:
                unitLabel && String(unitLabel).trim()
                  ? String(unitLabel).trim()
                  : "Non définie",
              unit_price: item.unit_price,
              discount_rate: item.discount_rate || 0,
              tax_id: item.tax_id,
              subtotal: item.subtotal,
            };
          });
        } else {
          console.warn(
            `[ExpandedRowContent] No items found in achatDetail for order ${orderId}`
          );
        }
        setItems(fetchedItems);
      } catch (error) {
        console.error(
          `[ExpandedRowContent] Error fetching details for order ${orderId}:`,
          error
        );
        message.error(`Erreur chargement détails achat ${orderId}`);
        setItems([]); // Ensure items is an empty array on error
      } finally {
        setLoadingDetails(false);
      }
    };
    fetchDetails();
  }, [orderId, api, globalState.produits]);

  const resolveUnitLabel = (item: any): string => {
    const resolvedProductId = item?.product_id ?? item?.produit_id;
    const productFromState = globalState.produits.find(
      (p: any) =>
        Number(p?.id) === Number(resolvedProductId) ||
        Number(p?.id_produit) === Number(resolvedProductId) ||
        Number(p?.product_id) === Number(resolvedProductId)
    );
    const unitId =
      item?.unit_id ??
      item?.unit?.id ??
      productFromState?.unit_id ??
      productFromState?.unit?.id;
    const mappedUnit =
      unitId !== undefined && unitId !== null
        ? unitsById[Number(unitId)]
        : undefined;

    const unitLabel =
      item?.unit_short_name ||
      item?.unit_name ||
      item?.unit?.short_name ||
      item?.unit?.name ||
      productFromState?.unit_short_name ||
      productFromState?.unit_name ||
      productFromState?.unit?.short_name ||
      productFromState?.unit?.name ||
      mappedUnit?.short_name ||
      mappedUnit?.name;

    return unitLabel && String(unitLabel).trim()
      ? String(unitLabel).trim()
      : "Non définie";
  };

  const detailColumns = [
    {
      title: "Produit",
      dataIndex: "nom_produit", // Use the pre-calculated nom_produit
      key: "produit",
      render: (text: string): string => text || "Produit inconnu",
    },
    {
      title: "Quantité",
      dataIndex: "quantity",
      key: "quantite",
      render: (value: any): string => formatQuantity(value),
    },
    {
      title: "Unité",
      dataIndex: "unit_label",
      key: "unite",
      render: (_: string, item: any): string => resolveUnitLabel(item),
    },
    {
      title: "Prix unitaire",
      dataIndex: "unit_price",
      key: "prix_unitaire",
      render: (value: any): string => `${formatUnitPrice(value)} CFA`,
    },
    {
      title: "Remise",
      dataIndex: "discount_rate",
      key: "remise",
      render: (value: any): string => `${formatNumber(value || 0)} CFA`,
    },
    {
      title: "Taxe",
      dataIndex: "tax_id",
      key: "taxe",
      render: (tax_id: any): string => {
        if (!tax_id) return "Aucune";
        const tax = globalState.taxes.find((t: any) => t.id === tax_id);
        return tax ? `${tax.name} (${tax.rate}%)` : "Inconnue";
      },
    },
    {
      title: "Total",
      dataIndex: "subtotal",
      key: "total",
      render: (value: any): string => `${formatNumber(value)} CFA`,
    },
  ];

  if (loadingDetails) {
    return (
      <div style={{ padding: "10px", textAlign: "center" }}>
        <Spin /> Chargement des détails...
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div style={{ padding: "10px", textAlign: "center", color: "grey" }}>
        Aucun produit trouvé pour cette commande.
      </div>
    );
  }

  return (
    <Table
      dataSource={items}
      columns={detailColumns}
      pagination={false}
      bordered
      size="small"
      rowKey={(item: any) => item.id} // Use the generated unique key
    />
  );
};

/* --- Composant principal GestionAchat --- */
export function PurchaseFormWorkspace({ order = null, orderType = "purchase", onClose, onSaved }: {
  order?: any; orderType?: PurchaseFlowOrderType; onClose: () => void; onSaved: () => Promise<void>;
}) {
  const [state, dispatch] = useReducer(achatsReducer, { ...initialState, showForm: true,
    formData: order ? purchaseFormData(order, order.items || order.produitsAches || [])
      : { ...initialState.formData, Date_Facture: dayjs().format("YYYY-MM-DD"), due_date: dayjs().format("YYYY-MM-DD"), produitsAches: [] } });
  return <AchatsContext.Provider value={{ state, dispatch, api }}>
    <AchatForm visible onClose={onClose} refreshAchats={onSaved} orderType={orderType} />
  </AchatsContext.Provider>;
}

const GestionAchat: React.FC<GestionAchatProps> = ({
  orderType = "purchase",
  moduleLabel,
  moduleLabelPlural,
}) => {
  // Utiliser le reducer importé depuis le module store/reducer
  const [state, dispatch] = useReducer(achatsReducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const consumedReplenishmentRequestRef = useRef<string | null>(null);
  const purchasesRequestRef = useRef(0);
  const [activeReplenishment, setActiveReplenishment] =
    useState<StockReplenishmentPrefill | null>(null);
  const [completedReplenishment, setCompletedReplenishment] =
    useState<StockReplenishmentPrefill | null>(null);
  const { selectedWarehouse, selectedCompany } = useSelection();
  const { taxes } = useTaxes(api, { companyId: selectedCompany });
  // Remplacer currentWarehouse par currentWarehouseId
  // const [currentWarehouse, setCurrentWarehouse] = useState<any>(null);
  const [currentWarehouseId, setCurrentWarehouseId] = useState<number | null>(
    null
  );
  // @ts-ignore - Context from JS file
  const { hasPermission, user } = useAuth();
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );
  const effectiveOrderType = normalizePurchaseFlowOrderType(orderType);
  const flowLabels = getFlowLabels(effectiveOrderType);
  const resolvedModuleLabel = moduleLabel || flowLabels.singular;
  const resolvedModuleLabelPlural = moduleLabelPlural || flowLabels.plural;
  const resolvedModuleLabelPluralTitle = capitalizeLabel(
    resolvedModuleLabelPlural
  );
  const resolvedPermissionRoot =
    ORDER_TYPE_PERMISSION_ROOT_MAP[effectiveOrderType];
  const showPayments = effectiveOrderType === "purchase";

  const clearReplenishmentRouteState = useCallback(() => {
    const routeState = (location.state || {}) as Record<string, any>;
    if (
      !routeState.stockReplenishment &&
      !routeState.productionReplenishment
    ) {
      return;
    }

    const nextRouteState = { ...routeState };
    discardSalesDraftFromReplenishment(
      routeState.stockReplenishment || routeState.productionReplenishment
    );
    delete nextRouteState.stockReplenishment;
    delete nextRouteState.productionReplenishment;
    consumedReplenishmentRequestRef.current = null;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: Object.keys(nextRouteState).length > 0 ? nextRouteState : null,
      }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  const handleReplenishmentCancelled = useCallback(() => {
    setActiveReplenishment(null);
    setCompletedReplenishment(null);
    clearReplenishmentRouteState();
  }, [clearReplenishmentRouteState]);

  const handleReplenishmentCompleted = useCallback(() => {
    const replenishment = activeReplenishment;
    setActiveReplenishment(null);

    if (isReturnableStockReplenishment(replenishment)) {
      setCompletedReplenishment(replenishment);
      return;
    }

    clearReplenishmentRouteState();
  }, [activeReplenishment, clearReplenishmentRouteState]);

  const handleStayAfterReplenishment = useCallback(() => {
    setCompletedReplenishment(null);
    clearReplenishmentRouteState();
  }, [clearReplenishmentRouteState]);

  const handleReturnAfterReplenishment = useCallback(
    (returnNavigation: { path: string; state: Record<string, any> }) => {
      setCompletedReplenishment(null);
      navigate(returnNavigation.path, {
        replace: true,
        state: returnNavigation.state,
      });
    },
    [navigate]
  );

  useEffect(() => {
    dispatch({ type: "SET_TAXES", payload: taxes });
  }, [taxes, selectedCompany]);

  // Gérer le changement de magasin sélectionné
  useEffect(() => {
    // Extraire l'ID
    const selectedId = selectedWarehouse?.id || selectedWarehouse;
    const currentId = currentWarehouseId;

    // Comparer les IDs pour éviter les boucles infinies
    if (String(selectedId) !== String(currentId)) {
      setCurrentWarehouseId(selectedId ? Number(selectedId) : null);
      // Réinitialiser la page à 1 pour montrer les nouveaux résultats depuis le début
      dispatch({ type: "SET_PAGE", payload: 1 });
    }
  }, [selectedWarehouse, currentWarehouseId]); // Depend on selectedWarehouse and the ID state

  // Appel initial de refreshAchats au montage du composant - seulement si un magasin est sélectionné
  useEffect(() => {
    // refreshAchats sera appelé automatiquement par l'effet de dépendance ci-dessous
    // quand currentWarehouseId sera disponible
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAchats = useCallback(async () => {
    const requestId = ++purchasesRequestRef.current;
    // Ne pas faire d'appel API si aucun magasin n'est sélectionné
    if (!currentWarehouseId) {
      dispatch({ type: "SET_ACHATS", payload: { achats: [], total: 0 } });
      dispatch({ type: "SET_LOADING", payload: false });
      return;
    }

    dispatch({ type: "SET_LOADING", payload: true });
    try {
      // Préparer les paramètres de requête
      const params: any = {
        page: state.currentPage,
        limit: state.itemsPerPage,
        search: state.searchTerm,
        include_deleted: state.showDeleted ? "true" : "false",
        order_type: effectiveOrderType,
        include_summary: showPayments ? "true" : undefined,
      };

      // Vérifier et ajouter company_id aux paramètres
      if (selectedCompany) {
        params.company_id = selectedCompany;
      } else {
        console.warn(
          `Attention: Aucune entreprise sélectionnée pour le filtrage des ${resolvedModuleLabelPlural}`
        );
      }

      // Traitement explicite du paramètre warehouse
      if (currentWarehouseId) {
        // Si currentWarehouse est un objet avec un id
        if (
          typeof currentWarehouseId === "number" ||
          typeof currentWarehouseId === "string"
        ) {
          params.warehouse = currentWarehouseId;
        }
      }

      // Ajouter le filtre de fournisseur s'il est sélectionné
      if (state.selectedFournisseur) {
        // 1. Récupérer le fournisseur sélectionné
        const selectedSupplier = state.selectedFournisseur;

        // 2. Déterminer l'ID à utiliser (préférer USER_ID s'il existe)
        let userIdToUse =
          selectedSupplier.USER_ID ||
          selectedSupplier.user_id ||
          selectedSupplier.id;

        // 3. Afficher des logs pour le débogage

        // 4. Utiliser l'ID approprié pour le filtrage
        params.fournisseur = userIdToUse;
      }

      // Ajouter les filtres de date s'ils sont spécifiés
      if (state.dateRange.start) {
        params.dateDebut = state.dateRange.start;
      }
      if (state.dateRange.end) {
        params.dateFin = state.dateRange.end;
      }

      // Ajouter le filtre de statut de paiement en fonction de l'onglet actif
      if (showPayments) {
        if (state.activeTab === "unpaid") {
          params.payment_status = "Non payé";
        } else if (state.activeTab === "partially_paid") {
          params.payment_status = "Partiellement payé";
        } else if (state.activeTab === "paid") {
          params.payment_status = "Payé";
        }
      }

      const response = await api.fetchAchats(params);
      if (requestId !== purchasesRequestRef.current) return;

      // Si le filtrage côté serveur ne fonctionne pas, filtrer côté client
      let filteredData = response.data;

      if (showPayments && state.activeTab !== "all" && Array.isArray(filteredData)) {
        if (state.activeTab === "unpaid") {
          filteredData = filteredData.filter(
            (achat) =>
              achat.payment_status === "Non payé" ||
              achat.due_amount === achat.total ||
              achat.paid_amount === 0
          );
        } else if (state.activeTab === "partially_paid") {
          filteredData = filteredData.filter(
            (achat) =>
              achat.payment_status === "Partiellement payé" ||
              (achat.paid_amount > 0 && achat.paid_amount < achat.total)
          );
        } else if (state.activeTab === "paid") {
          filteredData = filteredData.filter(
            (achat) =>
              achat.payment_status === "Payé" ||
              achat.paid_amount >= achat.total ||
              achat.due_amount === 0
          );
        }
      }

      // Trier les achats du plus récent au plus ancien (même logique que les ventes)
      if (Array.isArray(filteredData)) {
        filteredData.sort((a, b) => {
          const orderDateDiff =
            new Date(b.order_date).getTime() - new Date(a.order_date).getTime();
          if (orderDateDiff !== 0) return orderDateDiff;

          // Fallback stable si même order_date
          if (a.created_at && b.created_at) {
            const createdAtDiff =
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime();
            if (createdAtDiff !== 0) return createdAtDiff;
          }

          return (Number(b.id) || 0) - (Number(a.id) || 0);
        });
      }

      dispatch({
        type: "SET_ACHATS",
        payload: {
          achats: filteredData,
          total: response.total,
          summary: response.summary,
        },
      });
    } catch (error) {
      if (requestId !== purchasesRequestRef.current) return;
      console.error("Erreur lors du chargement des achats:", error);
      const errorMessage =
        error instanceof Error
          ? `Erreur lors du chargement des ${resolvedModuleLabelPlural}: ${error.message}`
          : `Erreur lors du chargement des ${resolvedModuleLabelPlural}`;

      dispatch({
        type: "SET_ERROR",
        payload: errorMessage,
      });

      message.error(errorMessage);
    } finally {
      if (requestId === purchasesRequestRef.current) {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }
  }, [
    effectiveOrderType,
    currentWarehouseId,
    showPayments,
    resolvedModuleLabelPlural,
    selectedCompany,
    user?.id,
    state.currentPage,
    state.itemsPerPage,
    state.searchTerm,
    state.showDeleted,
    state.selectedFournisseur,
    state.dateRange.start,
    state.dateRange.end,
    state.activeTab,
  ]);

  useEffect(() => {
    if (!showPayments && state.activeTab !== "all") {
      dispatch({ type: "SET_ACTIVE_TAB", payload: "all" });
    }
  }, [dispatch, showPayments, state.activeTab]);

  useEffect(() => {
    let active = true;
    const loadInitialData = async () => {
      // Si aucun magasin n'est sélectionné, réinitialiser les données
      if (!currentWarehouseId) {
        dispatch({ type: "SET_FOURNISSEURS", payload: [] });
        return;
      }

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const fournisseursResponse = await api.fetchFournisseurs(currentWarehouseId || undefined);
        const prefill = location.state?.stockReplenishment || location.state?.productionReplenishment;
        const ids = (prefill?.items || []).map((item: any) => Number(item.product_id)).filter((id: number) => id > 0);
        const selectedProducts: Product[] = [];
        for (let offset = 0; offset < ids.length; offset += 100) {
          const result = await fetchOrderFormOptions({ userId: user?.id, companyId: (selectedCompany as any)?.id ?? selectedCompany, warehouseId: currentWarehouseId },
            effectiveOrderType, "create", { kind: "products", item_ids: ids.slice(offset, offset + 100).join(",") });
          selectedProducts.push(...(result.products || []).map((product) => ({ ...product, product_id: product.product_id ?? undefined })));
        }
        if (!active) return;
        dispatch({ type: "SET_FOURNISSEURS", payload: fournisseursResponse });
        dispatch({ type: "SET_PRODUITS", payload: selectedProducts });
      } catch (error) {
        if (!active) return;
        console.error("Erreur chargement initial:", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Erreur lors du chargement des données",
        });
        message.error("Erreur lors du chargement des données");
      } finally {
        if (active) dispatch({ type: "SET_LOADING", payload: false });
      }
    };
    loadInitialData();
    return () => { active = false; };
  }, [currentWarehouseId, selectedCompany, user?.id, effectiveOrderType, location.state]);

  // Recharger à chaque filtre et ignorer les réponses du contexte précédent.
  useEffect(() => {
    refreshAchats();
    return () => { purchasesRequestRef.current += 1; };
  }, [refreshAchats]);

  useEffect(() => {
    const routeState = (location.state || {}) as Record<string, any>;
    const prefill = (routeState.stockReplenishment ||
      routeState.productionReplenishment) as
      | StockReplenishmentPrefill
      | undefined;

    if (!prefill || !currentWarehouseId || state.produits.length === 0) {
      return;
    }

    const requestId =
      prefill.request_id ||
      `${prefill.production_unit_id || "production"}-${
        prefill.warehouse_id || currentWarehouseId
      }`;
    if (consumedReplenishmentRequestRef.current === requestId) {
      return;
    }

    consumedReplenishmentRequestRef.current = requestId;

    if (
      prefill.target_order_type &&
      prefill.target_order_type !== effectiveOrderType
    ) {
      clearReplenishmentRouteState();
      return;
    }

    const sourceCompanyId = Number(prefill.company_id);
    const selectedCompanyId = Number(
      selectedCompany?.id ?? selectedCompany
    );
    if (
      Number.isFinite(sourceCompanyId) &&
      sourceCompanyId > 0 &&
      Number.isFinite(selectedCompanyId) &&
      selectedCompanyId > 0 &&
      sourceCompanyId !== selectedCompanyId
    ) {
      message.error(
        "L'entreprise sélectionnée ne correspond plus à celle du besoin de réapprovisionnement."
      );
      clearReplenishmentRouteState();
      return;
    }

    const sourceWarehouseId = Number(prefill.warehouse_id);
    if (
      Number.isFinite(sourceWarehouseId) &&
      sourceWarehouseId > 0 &&
      sourceWarehouseId !== Number(currentWarehouseId)
    ) {
      message.error(
        "L'établissement sélectionné ne correspond plus à celui du besoin de réapprovisionnement."
      );
      clearReplenishmentRouteState();
      return;
    }

    if (!hasPermission(`${resolvedPermissionRoot}.create`)) {
      message.error(
        `Vous n'avez pas la permission de créer un ${resolvedModuleLabel}.`
      );
      clearReplenishmentRouteState();
      return;
    }

    const replenishmentLines = mapStockReplenishmentItems(
      prefill.items,
      state.produits
    );

    if (replenishmentLines.length === 0) {
      message.warning(
        "Aucun produit avec une quantité manquante valide n'a été trouvé."
      );
      clearReplenishmentRouteState();
      return;
    }

    const sourceLabel =
      prefill.source_label ||
      prefill.production_unit_name ||
      (prefill.production_unit_id
        ? `production #${prefill.production_unit_id}`
        : "besoin de stock");
    const outputQuantity = toNumberWithDecimalSupport(
      prefill.output_quantity
    );
    const outputDetails =
      outputQuantity > 0 ? ` (${formatQuantity(outputQuantity)})` : "";
    const today = dayjs().format("YYYY-MM-DD");

    dispatch({
      type: "SET_FORM_DATA",
      payload: {
        ...initialState.formData,
        Date_Facture: today,
        due_date: today,
        Statut_Achat: "Commandé",
        warehouse_id: currentWarehouseId,
        produitsAches: replenishmentLines,
        remise_globale: 0,
        remarques: `Réapprovisionnement des stocks manquants pour ${sourceLabel}${outputDetails}.`,
      },
    });
    dispatch({ type: "TOGGLE_FORM", payload: true });
    setActiveReplenishment(prefill);
    setCompletedReplenishment(null);
    message.success(
      `Réapprovisionnement ajouté au panier : ${replenishmentLines.length} ${
        replenishmentLines.length === 1
          ? "produit"
          : "produits"
      }.`
    );
  }, [
    clearReplenishmentRouteState,
    currentWarehouseId,
    effectiveOrderType,
    hasPermission,
    location.state,
    resolvedModuleLabel,
    resolvedPermissionRoot,
    selectedCompany,
    state.produits,
    productionTerminology,
  ]);

  const openAchatForm = () => {
    if (!currentWarehouseId) {
      Modal.warning({
        title: "Établissement non sélectionné",
        content:
          `Veuillez sélectionner un établissement avant de créer ou modifier un ${resolvedModuleLabel}.`,
      });
      return;
    }
    // Synchroniser warehouse_id dans le state.formData
    dispatch({
      type: "UPDATE_FORM_DATA",
      payload: { warehouse_id: currentWarehouseId },
    });
    dispatch({ type: "TOGGLE_FORM", payload: true });
  };

  const resolveSupplierNameForExport = (achat: any): string => {
    const fournisseur = state.fournisseurs.find(
      (item: any) =>
        Number(item.id) === Number(achat.user_id) ||
        Number(item.user_id) === Number(achat.user_id) ||
        Number(item.USER_ID) === Number(achat.user_id)
    );
    return (
      achat.supplier_name ||
      achat.user_name ||
      achat.entity_name ||
      fournisseur?.name ||
      fournisseur?.company_name ||
      fournisseur?.Nom_Raison_Sociale ||
      "Fournisseur inconnu"
    );
  };

  /**
   * Construit un périmètre d'export identique pour Excel et PDF à partir des
   * filtres actifs de Gestion Achat.
   */
  const buildPurchaseExportRequest = () => {
    const params: any = {
      page: 1,
      limit: 1000,
      order_type: effectiveOrderType,
      search: state.searchTerm.trim() || undefined,
      include_deleted: state.showDeleted ? "true" : "false",
      include_summary: showPayments ? "true" : undefined,
    };
    const filtersApplied: string[] = [];
    let supplierName: string | null = null;
    let periodLabel: string | null = null;

    if (state.selectedFournisseur) {
      const selectedSupplier = state.selectedFournisseur;
      params.fournisseur =
        selectedSupplier.USER_ID ||
        selectedSupplier.user_id ||
        selectedSupplier.id;
      supplierName =
        selectedSupplier.name ||
        selectedSupplier.company_name ||
        selectedSupplier.Nom_Raison_Sociale ||
        `Fournisseur ID ${params.fournisseur}`;
      filtersApplied.push(`Fournisseur: ${supplierName}`);
    }

    if (state.searchTerm) {
      filtersApplied.push(`Recherche: "${state.searchTerm}"`);
    }

    if (state.dateRange.start) params.dateDebut = state.dateRange.start;
    if (state.dateRange.end) params.dateFin = state.dateRange.end;
    if (state.dateRange.start && state.dateRange.end) {
      periodLabel = `du ${state.dateRange.start} au ${state.dateRange.end}`;
      filtersApplied.push(`Période: ${periodLabel}`);
    }

    const companyId =
      typeof selectedCompany === "object"
        ? (selectedCompany as any)?.id
        : selectedCompany;
    if (companyId) params.company_id = companyId;

    if (currentWarehouseId) params.warehouse = currentWarehouseId;

    if (showPayments && state.activeTab !== "all") {
      const paymentStatusLabels: Record<string, string> = {
        unpaid: "Non payé",
        partially_paid: "Partiellement payé",
        paid: "Payé",
      };
      const paymentStatus = paymentStatusLabels[state.activeTab];
      if (paymentStatus) {
        params.payment_status = paymentStatus;
        filtersApplied.push(`Statut de paiement: ${paymentStatus}`);
      }
    }

    if (state.showDeleted) filtersApplied.push("Inclut les achats supprimés");

    const warehouseName =
      typeof selectedWarehouse === "object"
        ? (selectedWarehouse as any)?.name || null
        : null;
    const companyName =
      typeof selectedCompany === "object"
        ? (selectedCompany as any)?.name || null
        : null;

    return {
      params,
      filtersApplied,
      supplierName,
      periodLabel,
      warehouseName,
      companyId,
      companyName,
    };
  };

  // Fonction pour exporter les données vers Excel
  const handleExportToExcel = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    try {
      message.loading({
        content: "Préparation de l'export Excel...",
        key: "exportLoading",
      });

      const { params } = buildPurchaseExportRequest();

      // Récupérer les données
      const response = await api.fetchAchats(params);

      // Formater les données pour l'export
      const dataToExport = response.data.map((achat: any) => {
        const fournisseurName = resolveSupplierNameForExport(achat);

        const baseRow: any = {
          ID: achat.id,
          "N? Facture": achat.invoice_number,
          Reference: achat.reference || "-",
          Date: dayjs(achat.order_date).format("DD/MM/YYYY"),
          Fournisseur: fournisseurName,
          Établissement: achat.warehouse_name,
          "Statut d'achat": achat.order_status,
          Total: achat.total,
          "Créé le": achat.created_at
            ? dayjs(achat.created_at).format("DD/MM/YYYY")
            : "-",
        };

        if (showPayments) {
          baseRow["Statut de paiement"] = achat.payment_status;
          baseRow["Montant payé"] = achat.paid_amount;
          baseRow["Montant dû"] = achat.due_amount;
        } else {
          baseRow["Achats liés"] = achat.linked_purchase_count || 0;
        }

        if (effectiveOrderType === "purchase") {
          baseRow["Bon de commande"] = achat.purchase_order_invoice_number || "-";
        }

        return baseRow;
      });

      // Créer le fichier Excel
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, resolvedModuleLabelPluralTitle);

      // Définir les largeurs des colonnes
      const columnWidths = [
        { wch: 8 }, // ID
        { wch: 15 }, // N? Facture
        { wch: 15 }, // Référence
        { wch: 12 }, // Date
        { wch: 30 }, // Fournisseur
        { wch: 20 }, // Magasin
        { wch: 15 }, // Statut d'achat
        ...(showPayments
          ? [
              { wch: 20 }, // Statut de paiement
              { wch: 15 }, // Montant payé
              { wch: 15 }, // Montant dû
            ]
          : [{ wch: 15 }]), // Achats liés
        ...(effectiveOrderType === "purchase" ? [{ wch: 20 }] : []), // Bon de commande
        { wch: 15 }, // Total
        { wch: 12 }, // Créé le
      ];
      worksheet["!cols"] = columnWidths;

      // Télécharger le fichier
      const today = dayjs().format("YYYY-MM-DD");
      const exportPrefix = capitalizeLabel(resolvedModuleLabelPlural).replace(
        /\s+/g,
        "_"
      );
      XLSX.writeFile(workbook, `${exportPrefix}_${today}.xlsx`);

      message.success({
        content: "Export Excel réussi!",
        key: "exportLoading",
      });
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      message.error({
        content: "Erreur lors de l'export Excel",
        key: "exportLoading",
      });
    }
  };

  const handleExportToPdf = async (mode: "detail" | "summary") => {
    try {
      message.loading({
        content: "Préparation du Grand livre Fournisseur...",
        key: "exportPurchasePdfLoading",
      });

      const {
        params,
        filtersApplied,
        supplierName,
        periodLabel,
        warehouseName,
        companyId,
        companyName,
      } = buildPurchaseExportRequest();
      const response = await api.fetchAchats(params);
      const purchasesToExport = Array.isArray(response.data) ? response.data : [];

      if (purchasesToExport.length === 0) {
        message.warning({
          content: "Aucun achat trouvé avec les filtres appliqués",
          key: "exportPurchasePdfLoading",
        });
        return;
      }

      const rows = purchasesToExport.map((achat: any) => ({
        invoice_number: achat.invoice_number || "",
        order_date: achat.order_date
          ? dayjs(achat.order_date).format("DD/MM/YYYY")
          : "",
        client_name: resolveSupplierNameForExport(achat),
        total: Number(achat.total || 0),
        paid_amount: Number(achat.paid_amount || 0),
        due_amount: Number(
          achat.due_amount ??
            Math.max(Number(achat.total || 0) - Number(achat.paid_amount || 0), 0)
        ),
        payment_status: achat.payment_status || "",
      }));

      const calculatedSummary = rows.reduce(
        (acc, row) => {
          const status = String(row.payment_status || "").toLowerCase();
          acc.totalAmount += row.total;
          if (status.includes("partiel")) acc.partiallyPaidAmount += row.total;
          else if (status.includes("non") || status === "unpaid")
            acc.unpaidAmount += row.total;
          else if (status.includes("pay") || status === "paid")
            acc.paidAmount += row.total;
          return acc;
        },
        {
          totalAmount: 0,
          paidAmount: 0,
          partiallyPaidAmount: 0,
          unpaidAmount: 0,
        }
      );
      const normalizedSummary = normalizePurchaseSummary(response.summary);
      const hasServerSummary = Boolean(response.summary);

      const pdfResponse = await fetch(
        `${BASE_URL}/api/orders/generate-purchase-list-pdf`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            mode,
            company_id: companyId || null,
            warehouse_id: currentWarehouseId || null,
            rows,
            summary: {
              total_sales_count: hasServerSummary
                ? normalizedSummary.total_purchase_count
                : rows.length,
              total_sales_amount: hasServerSummary
                ? normalizedSummary.total_purchase_amount
                : calculatedSummary.totalAmount,
              paid_amount: hasServerSummary
                ? normalizedSummary.paid_amount
                : calculatedSummary.paidAmount,
              partially_paid_amount: hasServerSummary
                ? normalizedSummary.partially_paid_amount
                : calculatedSummary.partiallyPaidAmount,
              unpaid_amount: hasServerSummary
                ? normalizedSummary.unpaid_amount
                : calculatedSummary.unpaidAmount,
            },
            meta: {
              warehouseId: currentWarehouseId || null,
              warehouseName: warehouseName || null,
              companyId: companyId || null,
              companyName: companyName || null,
              periodLabel: periodLabel || null,
              supplierName: supplierName || null,
              filtersApplied,
              currency: "XOF",
            },
          }),
        }
      );

      if (!pdfResponse.ok) {
        throw await buildApiError(
          pdfResponse,
          "Impossible de générer le Grand livre Fournisseur"
        );
      }

      const blob = await pdfResponse.blob();
      const today = dayjs().format("YYYY-MM-DD");
      const suffix =
        mode === "summary" ? "resume-par-fournisseur" : "details-factures";
      const filename = `Grand_livre_fournisseurs_${suffix}_${today}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(url);

      message.success({
        content: `${rows.length} facture${rows.length > 1 ? "s" : ""} fournisseur exportée${
          rows.length > 1 ? "s" : ""
        } dans "${filename}"`,
        key: "exportPurchasePdfLoading",
        duration: 5,
      });
    } catch (error) {
      console.error("Erreur lors de l'export PDF des achats:", error);
      message.error({
        content: `Erreur lors de l'export PDF: ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`,
        key: "exportPurchasePdfLoading",
      });
    }
  };

  const hasActiveListFilters = Boolean(
    state.searchTerm ||
      state.selectedFournisseur ||
      state.dateRange.start ||
      state.dateRange.end ||
      state.showDeleted
  );
  const listPeriodLabel =
    state.dateRange.start && state.dateRange.end
      ? `${dayjs(state.dateRange.start).format("DD/MM/YYYY")} – ${dayjs(
          state.dateRange.end
        ).format("DD/MM/YYYY")}`
      : "Toutes périodes";
  const documentCount = showPayments
    ? state.purchaseSummary.total_purchase_count
    : state.achats.length;

  const resetListFilters = () => {
    dispatch({ type: "SET_SEARCH", payload: "" });
    dispatch({ type: "SET_SELECTED_FOURNISSEUR", payload: null });
    dispatch({
      type: "SET_DATE_RANGE",
      payload: { start: "", end: "" },
    });
    if (state.showDeleted) {
      dispatch({ type: "TOGGLE_SHOW_DELETED", payload: false });
    }
    dispatch({ type: "SET_PAGE", payload: 1 });
  };

  return (
    <AchatsContext.Provider value={{ state, dispatch, api }}>
      <Layout className="commercial-list-shell">
        <Layout.Content className="commercial-list-page">
          {/* Rendu conditionnel si aucun magasin n'est sélectionné */}
          {!currentWarehouseId ? (
            <Card className="p-sm md:p-md">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-md mb-md">
                <Typography.Title
                  level={3}
                  className="!mb-0 text-xl md:text-2xl"
                >
                  {`Gestion des ${resolvedModuleLabelPlural}`}
                </Typography.Title>
              </div>

              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <Empty
                  image={
                    <ExclamationCircleOutlined
                      style={{ fontSize: 64, color: "#faad14" }}
                    />
                  }
                  description={
                    <div>
                      <h3
                        style={{
                          fontSize: 18,
                          fontWeight: "bold",
                          marginBottom: 8,
                        }}
                      >
                        Aucun établissement sélectionné
                      </h3>
                      <p style={{ color: "#666", marginBottom: 16 }}>
                        Veuillez sélectionner un établissement dans la barre de
                        navigation pour créer et gérer les{" "}
                        {resolvedModuleLabelPlural}.
                      </p>
                      <Alert
                        message="Information importante"
                        description={`Les ${resolvedModuleLabelPlural} sont organisés par établissement. Chaque établissement dispose de ses propres fournisseurs, produits et historique. La sélection d'un établissement est nécessaire pour accéder à cette fonctionnalité.`}
                        type="info"
                        showIcon
                        style={{
                          textAlign: "left",
                          maxWidth: 500,
                          margin: "0 auto",
                        }}
                      />
                    </div>
                  }
                />
              </div>
            </Card>
          ) : (
            <>
              <header className="commercial-list-page__header">
                <div className="commercial-list-page__heading">
                  <h1>{resolvedModuleLabelPluralTitle}</h1>
                  <p>
                    {showPayments
                      ? "Suivre les achats, les règlements et les réceptions fournisseurs"
                      : `Suivre et préparer les ${resolvedModuleLabelPlural}`}
                  </p>
                  <span className="commercial-list-page__scope">
                    {formatNumber(documentCount)}{" "}
                    {documentCount === 1
                      ? resolvedModuleLabel
                      : resolvedModuleLabelPlural}
                    {!showPayments && " affichés"}
                    <span aria-hidden="true">•</span>
                    {listPeriodLabel}
                  </span>
                </div>

                <div className="commercial-list-page__actions">
                  {hasPermission(`${resolvedPermissionRoot}.create`) && (
                    <Button
                      type="primary"
                      size="large"
                      icon={<PlusOutlined />}
                      onClick={openAchatForm}
                      disabled={!currentWarehouseId}
                      className="commercial-list-page__primary-action"
                    >
                      {`Nouveau ${resolvedModuleLabel}`}
                    </Button>
                  )}
                </div>
              </header>

              {/* Synthese des achats */}
              {showPayments &&
                hasPermission(`${resolvedPermissionRoot}.view`) && (
                  <PurchaseSummaryPanel
                    summary={state.purchaseSummary}
                    moduleLabel={resolvedModuleLabel}
                    moduleLabelPlural={resolvedModuleLabelPlural}
                  />
                )}

              <section
                className="commercial-list-page__filters"
                aria-label={`Filtres des ${resolvedModuleLabelPlural}`}
              >
                <Input
                  value={state.searchTerm}
                  allowClear
                  placeholder={`Rechercher un numéro de ${resolvedModuleLabel} ou un fournisseur`}
                  prefix={<SearchOutlined />}
                  onChange={(e) =>
                    dispatch({ type: "SET_SEARCH", payload: e.target.value })
                  }
                  className="commercial-list-page__search"
                />
                <Select
                  placeholder="Tous les fournisseurs"
                  value={state.selectedFournisseur?.id ?? undefined}
                  allowClear
                  showSearch
                  optionFilterProp="searchLabel"
                  filterOption={(input, option: any) =>
                    matchesCommercialEntitySearch(option?.searchLabel, input)
                  }
                  onChange={(value: any) => {
                    const selectedFournisseur = state.fournisseurs.find(
                      (f: any) => f.id === value
                    );
                    dispatch({
                      type: "SET_SELECTED_FOURNISSEUR",
                      payload: selectedFournisseur || null,
                    });
                    dispatch({ type: "SET_PAGE", payload: 1 });
                  }}
                  className="commercial-list-page__entity-filter"
                >
                  {state.fournisseurs.map((fournisseur: any) => (
                    <Option
                      key={fournisseur.id}
                      value={fournisseur.id}
                      searchLabel={buildCommercialEntitySearchText(fournisseur)}
                    >
                      {getCommercialEntityDisplayName(
                        fournisseur,
                        "Fournisseur inconnu"
                      )}
                    </Option>
                  ))}
                </Select>
                <DatePicker.RangePicker
                  value={
                    state.dateRange.start && state.dateRange.end
                      ? [dayjs(state.dateRange.start), dayjs(state.dateRange.end)]
                      : null
                  }
                  format="DD/MM/YYYY"
                  allowClear
                  placeholder={["Début", "Fin"]}
                  onChange={(_dates: any, dateStrings: string[]) => {
                    dispatch({
                      type: "SET_DATE_RANGE",
                      payload: {
                        start: dateStrings[0] || "",
                        end: dateStrings[1] || "",
                      },
                    });
                    dispatch({ type: "SET_PAGE", payload: 1 });
                  }}
                  className="commercial-list-page__period-filter"
                />
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  disabled={!hasActiveListFilters}
                  onClick={resetListFilters}
                  className="commercial-list-page__reset"
                >
                  Réinitialiser
                </Button>
                <Badge
                  count={state.showDeleted ? 1 : 0}
                  size="small"
                  color="var(--blue-elsa)"
                >
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "deleted",
                          icon: <DeleteOutlined />,
                          label: state.showDeleted
                            ? `Masquer les ${resolvedModuleLabelPlural} supprimés`
                            : `Afficher les ${resolvedModuleLabelPlural} supprimés`,
                        },
                      ],
                      onClick: () =>
                        dispatch({
                          type: "TOGGLE_SHOW_DELETED",
                          payload: !state.showDeleted,
                        }),
                    }}
                  >
                    <Button
                      icon={<FilterOutlined />}
                      className={
                        state.showDeleted
                          ? "commercial-list-page__more-filters is-active"
                          : "commercial-list-page__more-filters"
                      }
                    >
                      Filtres
                    </Button>
                  </Dropdown>
                </Badge>
              </section>

              {/* Onglets avec bouton d'export a droite */}
              <div className="commercial-list-page__tabs-toolbar">
                <div className="commercial-list-page__tabs-slot">
                  {showPayments ? (
                    <Tabs
                      activeKey={state.activeTab}
                      onChange={(activeKey) =>
                        dispatch({ type: "SET_ACTIVE_TAB", payload: activeKey })
                      }
                      className="commercial-list-page__tabs"
                    >
                      <Tabs.TabPane
                        tab={`Tous les ${resolvedModuleLabelPlural} (${documentCount})`}
                        key="all"
                      />
                      <Tabs.TabPane tab="Non payés" key="unpaid" />
                      <Tabs.TabPane
                        tab="Partiellement payés"
                        key="partially_paid"
                      />
                      <Tabs.TabPane tab="Payés" key="paid" />
                    </Tabs>
                  ) : (
                    <Typography.Text strong>
                      {resolvedModuleLabelPluralTitle}
                    </Typography.Text>
                  )}
                </div>
                <div>
                  {hasPermission(`${resolvedPermissionRoot}.view`) && (
                    showPayments ? (
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: [
                            {
                              key: "excel",
                              icon: (
                                <FileExcelOutlined style={{ color: "#217346" }} />
                              ),
                              label: "Exporter sur Excel",
                              onClick: handleExportToExcel,
                            },
                            { type: "divider" },
                            {
                              key: "pdf-detail",
                              icon: (
                                <FilePdfOutlined style={{ color: "#d32029" }} />
                              ),
                              label: "Exporter en PDF (liste détaillée)",
                              onClick: () => handleExportToPdf("detail"),
                            },
                            {
                              key: "pdf-summary",
                              icon: (
                                <FilePdfOutlined style={{ color: "#d32029" }} />
                              ),
                              label: "Exporter en PDF (résumé par fournisseur)",
                              onClick: () => handleExportToPdf("summary"),
                            },
                          ],
                        }}
                      >
                        <Button
                          icon={<DownloadOutlined />}
                          className="commercial-list-page__export"
                        >
                          Exporter <DownOutlined />
                        </Button>
                      </Dropdown>
                    ) : (
                      <Button
                        icon={<FileExcelOutlined style={{ color: "#217346" }} />}
                        onClick={handleExportToExcel}
                        className="commercial-list-page__export"
                      >
                        Exporter sur Excel
                      </Button>
                    )
                  )}
                </div>
              </div>

              {/* Tableau principal */}
              <AchatsTable
                refreshAchats={refreshAchats}
                orderType={effectiveOrderType}
                permissionRoot={resolvedPermissionRoot}
                moduleLabel={resolvedModuleLabel}
                moduleLabelPlural={resolvedModuleLabelPlural}
                showPayments={showPayments}
              />
            </>
          )}

          {state.showForm && (
            <AchatForm
              visible={state.showForm}
              onClose={() => dispatch({ type: "TOGGLE_FORM", payload: false })}
              refreshAchats={refreshAchats}
              onReplenishmentCompleted={
                activeReplenishment
                  ? handleReplenishmentCompleted
                  : undefined
              }
              onReplenishmentCancelled={
                activeReplenishment
                  ? handleReplenishmentCancelled
                  : undefined
              }
              orderType={effectiveOrderType}
              moduleLabel={resolvedModuleLabel}
              moduleLabelPlural={resolvedModuleLabelPlural}
            />
          )}
          <StockReplenishmentReturnModal
            open={Boolean(completedReplenishment)}
            replenishment={completedReplenishment}
            method="purchase"
            onStay={handleStayAfterReplenishment}
            onReturn={handleReturnAfterReplenishment}
          />
        </Layout.Content>
      </Layout>
    </AchatsContext.Provider>
  );
};

export default GestionAchat;




